const PRODUCT_CONFIG = {
  description: {
    splitClass: 'js-read-more-marker',
    readMoreText: 'Learn More',
    readLessText: 'Read Less',
    selector: '.product__description'
  },
  addOn: {
    addToCartButton: '.pdp-atc',
    addonGroupSelector: '.addon-product-options',
    addonVariantSelector: '.addon-variant-radio',
    addonProductIdSelector: 'input[name="addon-product-id"]',
    cartDrawerSelector: 'cart-drawer',
    activeSwatchClass: 'swatch-active',
    activeButtonClass: 'variant-button-active',
    engravingHandle: 'stethoscope-engraving',
    engravingPropertyKey: 'Engraving Text',
    engravingMaxLength: 50,
    sellingPlanInput: '.addon-product-options input[name="selling_plan"]'
  }
};

// Product Description Manager Class
class ProductDescriptionManager {
  constructor(options = {}) {
    this.PRODUCT_CONFIG = {
      splitClass: options.splitClass || PRODUCT_CONFIG.description.splitClass,
      readMoreText: options.readMoreText || PRODUCT_CONFIG.description.readMoreText,
      readLessText: options.readLessText || PRODUCT_CONFIG.description.readLessText,
      selector: options.selector || PRODUCT_CONFIG.description.selector
    };
    this.init();
  }

  init() {
    this.descriptionElement = document.querySelector(this.PRODUCT_CONFIG.selector);
    if (!this.descriptionElement) {
      return;
    }

    const markerElement = this.descriptionElement.querySelector(`.${this.PRODUCT_CONFIG.splitClass}`);
    if (!markerElement) {
      return;
    }

    this.fullContent = this.descriptionElement.innerHTML;
    const parts = this.fullContent.split(markerElement.outerHTML);

    if (parts.length === 2) {
      this.visibleContent = parts[0];
      this.hiddenContent = parts[1];

      if (this.hiddenContent.trim()) {
        this.createButton();
        this.truncateText();
      }
    }
  }

  createButton() {
    this.button = document.createElement('button');
    this.button.className = 'read-more-button';
    this.button.setAttribute('aria-expanded', 'false');
    this.button.textContent = this.PRODUCT_CONFIG.readMoreText;
    this.button.addEventListener('click', () => this.toggleDescription());
  }

  truncateText() {
    this.descriptionElement.innerHTML = this.visibleContent;
    this.descriptionElement.appendChild(this.button);
    this.descriptionElement.classList.add('truncated');
  }

  toggleDescription() {
    const isExpanded = this.button.getAttribute('aria-expanded') === 'true';

    this.descriptionElement.innerHTML = isExpanded ? this.visibleContent : this.fullContent;
    this.button.textContent = isExpanded ? this.PRODUCT_CONFIG.readMoreText : this.PRODUCT_CONFIG.readLessText;
    this.button.setAttribute('aria-expanded', String(!isExpanded));

    this.descriptionElement.appendChild(this.button);
    this.descriptionElement.classList.toggle('truncated', isExpanded);
  }
}

// Add-On Product Manager
class AddOnProductManager {
  constructor() {
    this.addToCartButtons = document.querySelectorAll(PRODUCT_CONFIG.addOn.addToCartButton);
    this.addonGroups = document.querySelectorAll(PRODUCT_CONFIG.addOn.addonGroupSelector);
    this.cartDrawer = document.querySelector('cart-drawer');
    this.variantDataMap = new Map();
    
    // Get the first addon group - this will be our "main" product
    this.primaryAddonGroup = Array.from(this.addonGroups)[0];
    this.additionalAddonGroups = Array.from(this.addonGroups).slice(1);
    
    this.engravingInput = document.getElementById('engraving-input');
    
    // Process addon groups for configurator and hide_image attributes
    this.processAddonAttributes();    
    
    this.init();
    this.initEngraving();
    this.initHiddenImages(); // Initialize hidden images functionality

    this.initUrlUpdating();
    this.checkUrlForVariant(); // Check URL for variant param on page load
  }

  // Updated processAddonAttributes method (renamed from processConfiguratorAttributes)
  processAddonAttributes() {
    // Process each addon group to add data attributes from section settings
    const addonGroups = document.querySelectorAll(PRODUCT_CONFIG.addOn.addonGroupSelector);
    
    addonGroups.forEach(group => {
      // Look for attributes in the parent section
      const sectionBlock = group.closest('[data-section-type="main-product"]');
      if (!sectionBlock) return;
      
      // Find the addon product block that corresponds to this group by matching product ID
      const productId = group.dataset.productId;
      if (!productId) return;
      
      // Find all addon product blocks
      const addonBlocks = sectionBlock.querySelectorAll('[data-block-type="add_on_product"]');
      for (const block of addonBlocks) {
        // Check if this block matches our group's product ID
        const blockProductId = block.dataset.productId;
        if (blockProductId === productId) {
          // Extract configuration attributes
          const showConfig = block.dataset.showConfig === 'true';
          const configType = block.dataset.configType || '';
          const hideImage = block.dataset.hideImage === 'true';
          const noBundleDiscount = block.dataset.noBundleDiscount === 'true';
          
          // Add these attributes to the addon group element
          group.dataset.showConfig = showConfig;
          group.dataset.configType = configType;
          group.dataset.hideImage = hideImage;
          group.dataset.noBundleDiscount = noBundleDiscount;
          
          break;
        }
      }
    });
  }

  // Add this new method to handle reading from the URL and selecting the matching variant
  checkUrlForVariant() {
    if (!this.primaryAddonGroup) {
      return;
    }
    
    // Get the URL and check for variant parameter
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    if (!variantId) {
      return;
    }
        
    // Get the product ID for the primary addon group
    const productId = this.primaryAddonGroup.dataset.productId;
    if (!productId) {
      return;
    }
    
    // Get variant data for this product
    const variantData = this.variantDataMap.get(productId);
    if (!variantData?.variants) {
      return;
    }
    
    // Find the variant that matches the ID from the URL
    const matchingVariant = variantData.variants.find(variant => 
      variant.id.toString() === variantId.toString()
    );
    
    if (!matchingVariant) {
      return;
    }
    
    // Get the option value from the variant (first option for single-option product)
    const optionValue = matchingVariant.option1;
        
    // Find the radio button that matches this option value
    const matchingRadio = this.primaryAddonGroup.querySelector(
      `${PRODUCT_CONFIG.addOn.addonVariantSelector}[value="${optionValue}"]`
    );
    
    if (!matchingRadio) {
      return;
    }
    
    // If the matching radio is already checked, no need to change
    if (matchingRadio.checked) {
      return;
    }
    
    // Check the matching radio button
    matchingRadio.checked = true;
    
    // Update UI to show this option as selected
    const label = matchingRadio.closest('.variant-swatch-label, .variant-button-label');
    if (label) {
      // Remove active class from all siblings
      const optionGroup = label.closest('.addon-option');
      if (optionGroup) {
        optionGroup.querySelectorAll('.variant-swatch-label').forEach(l => {
          l.classList.remove(PRODUCT_CONFIG.addOn.activeSwatchClass);
        });
        
        optionGroup.querySelectorAll('.variant-button-label').forEach(l => {
          l.classList.remove(PRODUCT_CONFIG.addOn.activeButtonClass);
        });
      }
      
      // Add active class to selected label
      if (label.classList.contains('variant-swatch-label')) {
        label.classList.add(PRODUCT_CONFIG.addOn.activeSwatchClass);
      } else {
        label.classList.add(PRODUCT_CONFIG.addOn.activeButtonClass);
      }
    }
    
    // Trigger the change event to update images and other elements
    const event = new Event('change', { bubbles: true });
    matchingRadio.dispatchEvent(event);
  }  

  // Add this new method to handle URL updating
  initUrlUpdating() {
    // Only proceed if we have a primary addon group (main product)
    if (!this.primaryAddonGroup) {
      return;
    }
    
    // Get the product ID from the primary addon group
    const productId = this.primaryAddonGroup.dataset.productId;
    if (!productId) {
      return;
    }
    
    // Find all radio buttons in the primary addon group
    const variantRadios = this.primaryAddonGroup.querySelectorAll(PRODUCT_CONFIG.addOn.addonVariantSelector);
    
    // Add change event listener to each radio button
    variantRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          this.updateUrlWithVariant(radio);
        }
      });
    });
  }
  
  // Method to update the URL when variant changes
  updateUrlWithVariant(radio) {
    if (!radio || !radio.checked) return;
    
    // Get product ID and selected option value
    const productId = this.primaryAddonGroup.dataset.productId;
    const selectedValue = radio.value;
    
    // Skip if it's a "none" option
    if (radio.dataset.isNone === 'true' || selectedValue.toLowerCase() === 'none') {
      return;
    }
    
    // Get variant data for this product
    const variantData = this.variantDataMap.get(productId);
    if (!variantData?.variants) {
      return;
    }
    
    // Find the matching variant based on the selected option
    const selectedOptions = [selectedValue];
    const matchingVariant = variantData.variants.find(variant => 
      selectedOptions.every((option, index) => variant[`option${index + 1}`] === option)
    );
    
    if (!matchingVariant) {
      return;
    }
    
    const variantId = matchingVariant.id;
    
    // Update the URL with the variant ID
    this.updateUrlParam('variant', variantId);
  }
  
  // Helper method to update URL parameters
  updateUrlParam(key, value) {
    // Get current URL and create URL object
    const url = new URL(window.location.href);
    
    // Set or update the parameter
    url.searchParams.set(key, value);
    
    // Update the browser history without reloading the page
    window.history.replaceState({}, '', url.toString());
  }  

  // Improved label click handler to ensure proper re-click behavior
  initHiddenImages() {
    // Find all addon groups with hide_image enabled
    this.addonGroups.forEach(group => {
      const hideImage = group.dataset.hideImage === 'true';
      const productHandle = group.dataset.handle;
      
      if (hideImage && productHandle) {
        
        // Initially hide related images
        this.toggleProductImages(productHandle, false);
        
        // Add listeners to variant selections for this addon
        const radios = group.querySelectorAll(`${PRODUCT_CONFIG.addOn.addonVariantSelector}`);
        radios.forEach(radio => {
          radio.addEventListener('change', (e) => {
            const isNone = radio.dataset.isNone === 'true' || radio.value.toLowerCase() === 'none';
                        
            // Show images if a non-none variant is selected, hide if 'none' is selected
            this.toggleProductImages(productHandle, !isNone);
          });
        });
        
        // Check initial state - this is crucial
        const selectedRadio = group.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
        if (selectedRadio) {
          const isNone = selectedRadio.dataset.isNone === 'true' || selectedRadio.value.toLowerCase() === 'none';
          const selectedValue = selectedRadio.value;
                    
          // If a non-none option is already selected on load, show the images
          if (!isNone) {
            // Use setTimeout to ensure DOM is fully loaded
            setTimeout(() => {
              this.toggleProductImages(productHandle, true);
            }, 100);
          }
        }
      }
    });
    
    // More robust label click handler to ensure re-clicks work properly
    document.addEventListener('click', (e) => {
      const clickedLabel = e.target.closest('.variant-button-label, .variant-swatch-label');
      if (clickedLabel) {
        const radio = clickedLabel.querySelector('input[type="radio"]');
        if (radio) {
          const addonGroup = radio.closest('.addon-product-options');
          if (addonGroup && addonGroup.dataset.hideImage === 'true') {
            const productHandle = addonGroup.dataset.handle;
            const isNone = radio.dataset.isNone === 'true' || radio.value.toLowerCase() === 'none';
            
            // Critical change: ALWAYS force toggle whether the radio is already checked or not
            // This ensures re-clicking an already active option works properly            
            // Even shorter delay to ensure UI responsiveness
            setTimeout(() => {
              // Force toggle the images regardless of radio.checked state
              this.toggleProductImages(productHandle, !isNone);
            }, 10);
          }
        }
      }
    });
  }
  
  // Replace the existing toggleProductImages method with this variant-aware fix
  toggleProductImages(productHandle, show) {
    if (!productHandle) return;
    
    // Get overlay manager if it exists
    const overlayManager = window.galleryOverlay;
    
    if (show) {
      // For showing images, directly manage image visibility
      const mediaItems = document.querySelectorAll('.product__media-item');
      let foundImages = false;
      let firstMatchingImage = null;
      
      // Hide all images first when showing add-on images
      mediaItems.forEach(item => {
        item.classList.remove('is-active');
        item.style.setProperty('display', 'none', 'important');
      });
      
      // Now look through all media items and show matching ones
      mediaItems.forEach(item => {
        const img = item.querySelector('img');
        if (!img) return;
        
        const altText = img.alt || '';
        
        // Check for matching handle in alt text
        const isMatchingImage = 
          altText.includes(`handle: ${productHandle}`) || 
          altText.includes(`handle:${productHandle}`) ||
          altText.includes(productHandle);
        
        if (isMatchingImage) {
          foundImages = true;
          
          // Reset display property to original value or default to block
          item.style.display = item.dataset.originalDisplay || 'block';
          // Also explicitly remove the !important style if it was set
          item.style.removeProperty('display');
          item.style.setProperty('display', 'block', 'important');
          
          // Keep track of the first matching image for activation
          if (!firstMatchingImage) {
            firstMatchingImage = item;
          }
        }
      });
      
      // Make the first matching image active if we found any
      if (firstMatchingImage) {
        // Remove active class from all images first
        mediaItems.forEach(item => item.classList.remove('is-active'));
        // Add active class to our first matching image
        firstMatchingImage.classList.add('is-active');
      }
      
      // Update overlay slides if overlay manager exists
      if (overlayManager) {
        setTimeout(() => {
          overlayManager.updateFilteredSlides();
        }, 100);
      }
      
      // Also update thumbnails - the original method will keep other thumbnails visible
      this.updateThumbnailsForProductHandle(productHandle, true);
      return;
    }
    
    // For hiding images (when show=false)
    const mediaItems = document.querySelectorAll('.product__media-item');
    let foundImages = false;
    let wasActiveHidden = false;
    
    // First, determine the current variant
    const currentVariant = this.currentMainProduct ? this.currentMainProduct.toLowerCase() : null;
    
    mediaItems.forEach(item => {
      const img = item.querySelector('img');
      if (!img) return;
      
      const altText = img.alt || '';
      
      // Check both "handle: xyz" format and any text containing the handle
      const isMatchingImage = 
        altText.includes(`handle: ${productHandle}`) || 
        altText.includes(`handle:${productHandle}`) ||
        altText.includes(productHandle);
      
      if (isMatchingImage) {
        foundImages = true;
        
        // Check if this was active before hiding
        if (item.classList.contains('is-active')) {
          wasActiveHidden = true;
          item.classList.remove('is-active');
        }
        
        // Store original display value if not already stored
        if (!item.dataset.originalDisplay) {
          item.dataset.originalDisplay = item.style.display || '';
        }
        
        // Set display property to none
        item.style.setProperty('display', 'none', 'important');
      }
    });
    
    // Also update the overlay gallery slides with the same filtering
    const overlaySlides = document.querySelectorAll('.overlay-gallery__slide');
    overlaySlides.forEach(slide => {
      const img = slide.querySelector('img');
      if (!img) return;
      
      const altText = img.alt || '';
      
      // Use the same matching logic as the main gallery
      const isMatchingImage = 
        altText.includes(`handle: ${productHandle}`) || 
        altText.includes(`handle:${productHandle}`) ||
        altText.includes(productHandle);
      
      if (isMatchingImage) {
        slide.dataset.visible = !show ? 'false' : 'true';
      }
    });
    
    // Also update thumbnails to match the hidden images
    this.updateThumbnailsForProductHandle(productHandle, false);
    
    // If we hid the active image, activate another image - PRIORITIZING the current variant
    if (wasActiveHidden) {
      // IMPROVED APPROACH: First collect ALL potential images to choose from
      const candidateImages = [];
      
      mediaItems.forEach(item => {
        const img = item.querySelector('img');
        if (!img) return;
        
        const altText = img.alt || '';
        
        // Skip if this is a case/addon image
        if (altText.includes('handle:') || altText.includes('handle: ')) return;
        
        // Skip if this is a configurator overlay image
        if (altText.includes('for config_eartips') || altText.includes('for config_chestpiece')) return;
        
        // Check if this is a base config image (highest priority for the current variant)
        const isConfigBase = altText.includes('for config_main');
        
        // Check if this is a variant image 
        const isVariantSpecific = altText.toLowerCase().includes(`color: ${currentVariant}`);
        
        // Check if this is a generic image
        const isGenericImage = !altText.includes('Color:');
        
        // Assign priority scores
        let priority = 0;
        
        if (isConfigBase && isVariantSpecific) {
          priority = 3; // Highest priority: Config base image for current variant
        } else if (isVariantSpecific) {
          priority = 2; // High priority: Regular image for current variant
        } else if (isConfigBase) {
          priority = 1; // Medium priority: Config base for any variant
        } else if (isGenericImage) {
          priority = 0; // Lowest priority: Generic product image
        } else {
          priority = -1; // Other variant images - only use if nothing else available
        }
        
        // Add to candidates with priority
        candidateImages.push({
          element: item,
          priority: priority,
          mediaId: item.dataset.mediaId
        });
      });
      
      // Sort candidates by priority (highest first)
      candidateImages.sort((a, b) => b.priority - a.priority);
      
      // Select the highest priority image
      if (candidateImages.length > 0) {
        const bestImage = candidateImages[0].element;
        
        // Make this image active and forcefully visible
        mediaItems.forEach(item => item.classList.remove('is-active'));
        bestImage.classList.add('is-active');
        
        // CRITICAL: Force display to ensure visibility
        bestImage.style.removeProperty('display');
        bestImage.style.setProperty('display', 'block', 'important');
        
        // Update thumbnails
        const mediaId = bestImage.dataset.mediaId;
        document.querySelectorAll('.thumbnail-list__item').forEach(thumb => {
          thumb.classList.remove('is-active');
          if (thumb.dataset.target === mediaId) {
            thumb.classList.add('is-active');
            thumb.style.removeProperty('display');
            thumb.style.setProperty('display', 'block', 'important');
          }
        });
      }
    }
    
    // Final safety check - ensure active image is actually visible
    setTimeout(() => {
      const activeImage = document.querySelector('.product__media-item.is-active');
      if (activeImage && window.getComputedStyle(activeImage).display === 'none') {
        // If active image is still hidden, forcefully make it visible
        activeImage.style.removeProperty('display');
        activeImage.style.setProperty('display', 'block', 'important');
      }
    }, 50);
    
    // Update overlay manager if it exists
    if (overlayManager) {
      overlayManager.updateFilteredSlides();
    }
  }

  // Add this new method to the AddOnProductManager class
  setupThumbnailClickHandling(productHandle) {
    // Don't add duplicate handlers - use a flag on the thumbnailList
    const thumbnailList = document.querySelector('.thumbnail-list');
    if (!thumbnailList || thumbnailList.dataset.handlerInitialized) return;
    
    // Mark that we've initialized this handler
    thumbnailList.dataset.handlerInitialized = 'true';
        
    // Store all handles that need hiding for reference
    this.handlesToHide = this.handlesToHide || [];
    if (!this.handlesToHide.includes(productHandle)) {
      this.handlesToHide.push(productHandle);
    }
    
    // Create ONE event listener for all thumbnails
    thumbnailList.addEventListener('click', (e) => {
      const thumbnailItem = e.target.closest('.thumbnail-list__item');
      if (!thumbnailItem) return;
      
      // Get the media ID this thumbnail points to
      const mediaId = thumbnailItem.dataset.target;
      if (!mediaId) return;
      
      // Find the target media item
      const targetMediaItem = document.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
      if (!targetMediaItem) return;
      
      // Get alt text of target image
      const targetImg = targetMediaItem.querySelector('img');
      const targetAltText = targetImg?.alt || '';
      
      // Check if clicked thumbnail is for an add-on product
      let isAddonThumbnail = false;
      for (const handle of this.handlesToHide) {
        if (targetAltText.includes(handle)) {
          isAddonThumbnail = true;
          break;
        }
      }
      
      // If clicked a regular product thumbnail (not an add-on), hide all add-on images
      if (!isAddonThumbnail) {
        
        // Hide all media items that match handles that should be hidden
        const allMediaItems = document.querySelectorAll('.product__media-item');
        
        allMediaItems.forEach(item => {
          const img = item.querySelector('img');
          if (!img) return;
          
          const altText = img.alt || '';
          
          // Check if this contains any handle that should be hidden
          for (const handle of this.handlesToHide) {
            if (altText.includes(handle)) {
              item.style.setProperty('display', 'none', 'important');
              break;
            }
          }
        });
      }
    });
  }
  
  // Replace the existing activateFirstVisibleImage method with this enhanced version
  activateFirstVisibleImage() {
    // First, get all media items
    const mediaItems = document.querySelectorAll('.product__media-item');
    
    // Check if any image is currently active
    const hasActiveImage = document.querySelector('.product__media-item.is-active');
    
    // Only proceed if no image is currently active
    if (!hasActiveImage) {
      // First, try to find an image that's NOT an add-on product
      const regularImage = Array.from(mediaItems).find(item => {
        // Skip if hidden
        if (item.style.display === 'none') return false;
        
        // Check if this is an add-on image
        const img = item.querySelector('img');
        if (!img) return false;
        
        const altText = img.alt || '';
        // Return true for images that don't have "handle:" in their alt text
        return !altText.includes('handle:') && !altText.includes('handle: ');
      });
      
      if (regularImage) {
        // Make this regular image active
        mediaItems.forEach(item => item.classList.remove('is-active'));
        regularImage.classList.add('is-active');
        
        // Also update thumbnails
        const mediaId = regularImage.dataset.mediaId;
        document.querySelectorAll('.thumbnail-list__item').forEach(thumb => {
          thumb.classList.remove('is-active');
          if (thumb.dataset.target === mediaId) {
            thumb.classList.add('is-active');
          }
        });
        
        return;
      }
      
      // Fallback: find any visible image
      const visibleImage = document.querySelector('.product__media-item[style*="display: block"], .product__media-item:not([style*="display: none"])');
      if (visibleImage) {
        visibleImage.classList.add('is-active');
        
        // Also update thumbnails
        const mediaId = visibleImage.dataset.mediaId;
        document.querySelectorAll('.thumbnail-list__item').forEach(thumb => {
          thumb.classList.remove('is-active');
          if (thumb.dataset.target === mediaId) {
            thumb.classList.add('is-active');
          }
        });
      }
    }
  }

  // Updated function to properly handle thumbnails with the product handle
  updateThumbnailsForProductHandle(productHandle, show) {
    const isMainProductView = document.querySelector('.pdp-main-thumbnails') !== null;
    const thumbnails = document.querySelectorAll('.thumbnail-list__item');
    let foundThumbnails = false;
    let firstMatchingThumbnail = null;
    
    // IMPORTANT CHANGE: When showing add-on thumbnails in the main product view,
    // don't hide all other thumbnails first. This allows all thumbnails to remain visible.
    if (show && !isMainProductView) {
      // Only hide all thumbnails if NOT in main product view
      thumbnails.forEach(thumb => {
        // Store original display value if not already stored
        if (!thumb.dataset.originalDisplay) {
          thumb.dataset.originalDisplay = thumb.style.display || '';
        }
        
        // Hide all thumbnails first
        thumb.style.setProperty('display', 'none', 'important');
      });
    }
    
    // Now process each thumbnail to show only those matching the handle
    thumbnails.forEach(thumb => {
      // Get the media ID this thumbnail corresponds to
      const mediaId = thumb.dataset.target;
      
      // Find the related media item
      const mediaItem = document.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
      if (!mediaItem) return;
      
      // Get the image inside the media item
      const img = mediaItem.querySelector('img');
      if (!img) return;
      
      // Check the alt text to see if it contains the product handle
      const altText = img.alt || '';
      
      // Check if this thumbnail is for the product handle
      const isMatchingThumbnail = 
        altText.includes(`handle: ${productHandle}`) || 
        altText.includes(`handle:${productHandle}`) ||
        altText.includes(productHandle);
      
      if (isMatchingThumbnail) {
        foundThumbnails = true;
        
        // Store original display value if not already stored
        if (!thumb.dataset.originalDisplay) {
          thumb.dataset.originalDisplay = thumb.style.display || '';
        }
        
        // Set visibility based on show parameter
        if (show) {
          // Show the thumbnail
          thumb.style.display = thumb.dataset.originalDisplay || '';
          
          // Keep track of first matching thumbnail
          if (!firstMatchingThumbnail) {
            firstMatchingThumbnail = thumb;
          }
        } else {
          // Hide the thumbnail
          thumb.style.setProperty('display', 'none', 'important');
          
          // If this thumbnail was active, need to make another thumbnail active
          if (thumb.classList.contains('is-active')) {
            thumb.classList.remove('is-active');
            this.activateFirstVisibleThumbnail();
          }
        }
      }
    });
    
    // Make the first matching thumbnail active in main product view if we're showing
    if (show && firstMatchingThumbnail && isMainProductView) {
      thumbnails.forEach(thumb => thumb.classList.remove('is-active'));
      firstMatchingThumbnail.classList.add('is-active');
    }
    
    // Make sure at least one thumbnail is active if we're hiding
    if (!show && foundThumbnails) {
      this.activateFirstVisibleThumbnail();
    }
    
    return foundThumbnails;
  } 
  
  updateThumbnailsForHiddenImages() {
    const mediaItems = document.querySelectorAll('.product__media-item');
    const thumbnails = document.querySelectorAll('.thumbnail-list__item');
    
    // Hide thumbnails for hidden media items
    thumbnails.forEach(thumb => {
      const targetMediaId = thumb.dataset.target;
      const targetMedia = document.querySelector(`.product__media-item[data-media-id="${targetMediaId}"]`);
      
      if (targetMedia) {
        const isHidden = targetMedia.style.display === 'none';
        // Store original display value if not already stored
        if (!thumb.dataset.originalDisplay) {
          thumb.dataset.originalDisplay = thumb.style.display || 'block';
        }
        
        // Match thumbnail visibility with media item
        thumb.style.display = isHidden ? 'none' : (thumb.dataset.originalDisplay || 'block');
        
        // If this thumbnail was active but its media is now hidden, make first visible thumbnail active
        if (isHidden && thumb.classList.contains('is-active')) {
          thumb.classList.remove('is-active');
          this.activateFirstVisibleThumbnail();
        }
      }
    });
    
    // Make sure at least one thumbnail is active if any are visible
    if (!document.querySelector('.thumbnail-list__item.is-active')) {
      this.activateFirstVisibleThumbnail();
    }
  }
  
  activateFirstVisibleThumbnail() {
    const visibleThumb = document.querySelector('.thumbnail-list__item[style*="display: block"], .thumbnail-list__item:not([style*="display: none"])');
    if (visibleThumb) {
      visibleThumb.classList.add('is-active');
      
      // Also activate corresponding media item
      const targetMediaId = visibleThumb.dataset.target;
      const targetMedia = document.querySelector(`.product__media-item[data-media-id="${targetMediaId}"]`);
      if (targetMedia) {
        // Remove active class from all media items
        document.querySelectorAll('.product__media-item').forEach(item => {
          item.classList.remove('is-active');
        });
        
        // Add active class to this media item
        targetMedia.classList.add('is-active');
      }
    }
  }

  // Add this new method to the AddOnProductManager class
  filterMainProductImages(variant) {
    
    // Get all media items and thumbnails
    const mediaItems = document.querySelectorAll('.product__media-item');
    const thumbnails = document.querySelectorAll('.thumbnail-list__item');
    
    // Track matching images for priority selection
    let matchingImages = [];
    
    // First hide all images
    mediaItems.forEach(item => {
      item.classList.remove('is-active');
      item.style.display = 'none';
    });
    
    // Show images matching this variant or generic images
    mediaItems.forEach(item => {
      const img = item.querySelector('img');
      if (!img) return;
      
      const altText = img.alt || '';
      
      // Check for config base images first (prioritize these)
      const isConfigBaseImage = altText.includes('for config_main') && 
                               altText.toLowerCase().includes(`color: ${variant}`);
                               
      // Then check for regular variant images
      const isVariantImage = altText.toLowerCase().includes(`color: ${variant}`) && 
                            !altText.includes('for config_');
                            
      // Also show generic images without color specification
      const isGenericImage = !altText.includes('Color:');
      
      // Skip addon product images
      const isAddonImage = altText.includes('handle:') || altText.includes('handle: ');
      
      if ((isConfigBaseImage || isVariantImage || isGenericImage) && !isAddonImage) {
        // Show this image
        item.style.display = item.dataset.originalDisplay || '';
        
        // Track matching images with priority
        if (isConfigBaseImage) {
          matchingImages.push({ item, priority: 2 }); // Highest priority
        } else if (isVariantImage) {
          matchingImages.push({ item, priority: 1 }); // Medium priority
        } else if (isGenericImage) {
          matchingImages.push({ item, priority: 0 }); // Lowest priority
        }
      }
    });
    
    // Sort matching images by priority
    matchingImages.sort((a, b) => b.priority - a.priority);
    
    // Activate the highest priority image
    if (matchingImages.length > 0) {
      const highestPriorityImage = matchingImages[0].item;
      highestPriorityImage.classList.add('is-active');
      
      // Update thumbnails
      const mediaId = highestPriorityImage.dataset.mediaId;
      thumbnails.forEach(thumb => {
        thumb.classList.remove('is-active');
        
        // Show/hide thumbnails based on corresponding media
        const thumbMediaId = thumb.dataset.target;
        const thumbMedia = document.querySelector(`.product__media-item[data-media-id="${thumbMediaId}"]`);
        if (thumbMedia) {
          thumb.style.display = thumbMedia.style.display;
        }
        
        // Set active thumbnail
        if (thumb.dataset.target === mediaId) {
          thumb.classList.add('is-active');
          thumb.style.display = thumb.dataset.originalDisplay || '';
        }
      });
    }
  
    const activeSlide = document.querySelector('.product__media-item.is-active');
    if (activeSlide) {
      activeSlide.style.setProperty('display', 'block', 'important');
    }
  }  

  // Debugging utility to log all images in the gallery
  logAllProductImages() {
    const mediaItems = document.querySelectorAll('.product__media-item');
    
    if (mediaItems.length === 0) {
      return;
    }
    
    mediaItems.forEach((item, index) => {
      const img = item.querySelector('img');
      if (!img) {
        return;
      }
      
      const altText = img.alt || '';
      const src = img.src || '';
      const displayStyle = window.getComputedStyle(item).display;
      const isActive = item.classList.contains('is-active');
      
    });
    
    // Log thumbnails as well
    const thumbnails = document.querySelectorAll('.thumbnail-list__item');
    
    if (thumbnails.length === 0) {
      return;
    }
    
    thumbnails.forEach((thumb, index) => {
      const mediaId = thumb.dataset.target;
      const displayStyle = window.getComputedStyle(thumb).display;
      const isActive = thumb.classList.contains('is-active');
    });
  }
  
  // Also add this helper function to directly force show images for a product handle
  forceShowProductImages(handle) {
    if (!handle) {
      return;
    }
        
    // Find all matching images
    const mediaItems = document.querySelectorAll('.product__media-item');
    const matchingImages = [];
    
    mediaItems.forEach(item => {
      const img = item.querySelector('img');
      if (!img) return;
      
      const altText = img.alt || '';
      
      if (altText.includes(handle)) {
        matchingImages.push(item);
        
        // Force display
        item.style.setProperty('display', 'block', 'important');
      }
    });
    
    // Make first matching image active
    if (matchingImages.length > 0) {
      mediaItems.forEach(item => item.classList.remove('is-active'));
      matchingImages[0].classList.add('is-active');
      
      // Also show and activate corresponding thumbnail
      const mediaId = matchingImages[0].dataset.mediaId;
      const thumbnail = document.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]`);
      
      if (thumbnail) {
        document.querySelectorAll('.thumbnail-list__item').forEach(t => t.classList.remove('is-active'));
        thumbnail.style.setProperty('display', 'block', 'important');
        thumbnail.classList.add('is-active');
      }
    }
  }  
  
  initEngraving() {
    // Just add basic monitoring of the engraving state
    this.engravingRadios = document.querySelectorAll('input[name="engraving-enabled"]');
    
    if (this.engravingRadios.length) {
      // Listen for changes
      this.engravingRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          const isEnabled = e.target.value === 'enabled';
          
          // If enabled, check for text in the hidden field
          if (isEnabled) {
            const textInput = document.querySelector('input[name="engraving-text"]');
            this.handleEngravingInput(textInput ? textInput.value : '');
          } else {
            this.handleEngravingInput('');
          }
        });
      });
    }
  }

  handleEngravingInput(value) {
    // Store the engraving text with the selected variant
    if (this.primaryAddonGroup) {
      const selectedVariant = this.primaryAddonGroup.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
      if (selectedVariant) {
        if (value && value.trim()) {
          selectedVariant.dataset.engravingText = value.trim();
        } else {
          delete selectedVariant.dataset.engravingText;
        }
      }
    }
  } 

  init() {
    
    // Check if cart type is drawer
    this.isDrawerCart = Boolean(document.querySelector('cart-drawer'));

    if (!this.addToCartButtons.length) {
      return;
    }

    // Initialize the primary product (first addon)
    if (this.primaryAddonGroup) {
      // Hide any "none" options for the primary product
      const noneOptions = this.primaryAddonGroup.querySelectorAll(`${PRODUCT_CONFIG.addOn.addonVariantSelector}[data-is-none="true"]`);
      noneOptions.forEach(option => {
        const label = option.closest('.variant-swatch-label, .variant-button-label');
        if (label) {
          label.style.display = 'none';
        }
      });

      // Select first non-none option if none is selected
      const selectedOption = this.primaryAddonGroup.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
      if (!selectedOption || selectedOption.dataset.isNone === 'true') {
        const firstNonNoneOption = Array.from(
          this.primaryAddonGroup.querySelectorAll(PRODUCT_CONFIG.addOn.addonVariantSelector)
        ).find(radio => radio.dataset.isNone !== 'true');
        
        if (firstNonNoneOption) {
          firstNonNoneOption.checked = true;
          const label = firstNonNoneOption.closest('.variant-swatch-label, .variant-button-label');
          if (label) {
            if (label.classList.contains('variant-swatch-label')) {
              label.classList.add(PRODUCT_CONFIG.addOn.activeSwatchClass);
            } else {
              label.classList.add(PRODUCT_CONFIG.addOn.activeButtonClass);
            }
          }
        }
      }
    }

    // Process no_bundle_discount attribute from section block
    this.addonGroups.forEach(group => {
      const addonBlockElements = document.querySelectorAll('[data-block-type="add_on_product"]');
      addonBlockElements.forEach(blockElement => {
        const productId = blockElement.dataset.productId;
        const noBundleDiscount = blockElement.dataset.noBundleDiscount === 'true';
  
        if (productId && group.dataset.productId === productId) {
          group.dataset.noBundleDiscount = noBundleDiscount;
        }
      });
    });    

    // Load variant data for each add-on product
    const variantDataElements = document.querySelectorAll('[id^="addon-variant-data-"]');
    variantDataElements.forEach(element => {
      try {
        const variantData = JSON.parse(element.textContent);
        const productId = element.id.replace('addon-variant-data-', '');
        
        const sellingPlanInput = document.querySelector(`.addon-product-options[data-product-id="${productId}"] input[name="selling_plan"]`);
        const sellingPlanId = sellingPlanInput ? sellingPlanInput.value : null;
        
        variantData.sellingPlanId = sellingPlanId;
        
        this.variantDataMap.set(productId, variantData);
      } catch (e) {
      }
    });

    // Add click handler to the add to cart button
    this.addToCartButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        
        e.preventDefault();
        e.stopPropagation();
        
        const buttonTextElement = button.querySelector('span');
        const originalText = buttonTextElement?.textContent || '';
        const loadingSpinner = button.querySelector('.loading-spinner');
        
        try {
          if (loadingSpinner) loadingSpinner.classList.remove('hidden');
          if (buttonTextElement) buttonTextElement.textContent = 'Adding...';
          button.setAttribute('disabled', 'disabled');
          
          await this.addProductsToCart();
          
          if (loadingSpinner) loadingSpinner.classList.add('hidden');
          if (buttonTextElement) buttonTextElement.textContent = originalText;
          button.removeAttribute('disabled');
        } catch (error) {
          if (loadingSpinner) loadingSpinner.classList.add('hidden');
          if (buttonTextElement) buttonTextElement.textContent = originalText;
          button.removeAttribute('disabled');
          console.error('Error adding to cart:', error);
        }
      });
    });

    // Set up change event listeners for addon variants
    this.addonGroups.forEach((group) => {
      const radios = group.querySelectorAll(PRODUCT_CONFIG.addOn.addonVariantSelector);
      radios.forEach((radio) => {
        radio.addEventListener('change', () => this.onVariantChange(radio));
      });
    });

    this.addonGroups.forEach(group => {
      const checkedRadio = group.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
      if (checkedRadio) {
        this.updateAddonPriceDisplay(checkedRadio);
      }
    });

    if (this.primaryAddonGroup) {
      const initialVariant = this.primaryAddonGroup.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
      if (initialVariant && initialVariant.value !== 'none') {
        
        // Check if we have an addon-product-options with data-show-config="true"
        const hasConfigurators = document.querySelector('.addon-product-options[data-show-config="true"]') !== null;
        
        if (!hasConfigurators) {
          // Only directly filter images if there's no configurator
          this.filterMainProductImages(initialVariant.value.toLowerCase());
        }
        // If there is a configurator, don't do anything here - let the configurator handle it
      }
    }
    
  }

  formatMoney(cents) {
    // Check if we're in the UK or Canada
    const isUK = this.isUKCountry();
    const isCanada = this.isCanadaCountry();
    
    // Get current Shopify currency or use appropriate fallback
    const currentCurrency = Shopify?.currency?.active || (isUK ? 'GBP' : (isCanada ? 'CAD' : 'USD'));
  
    let formattedValue;
    
    // If Shopify's formatMoney is available, use it first
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      formattedValue = Shopify.formatMoney(cents);
    } else {
      // Fallback to browser's Intl formatter with correct locale and currency
      formattedValue = new Intl.NumberFormat(isUK ? 'en-GB' : (isCanada ? 'en-CA' : undefined), {
        style: 'currency',
        currency: currentCurrency
      }).format(cents/100);
    }
    
    // Remove trailing .0 or .00
    formattedValue = formattedValue.replace(/\.0{1,2}(?=\s*$|$)/, '');
    
    // Special formatting for CAD - ensure it shows as "$XXX CAD" not "CA$XXX"
    if (isCanada && currentCurrency === 'CAD' && !formattedValue.includes('CAD')) {
      formattedValue = formattedValue.replace(/^CA\$/, '$');
      formattedValue += '';
    }
    
    // Special formatting for GBP - ensure it shows as "£XXX GBP" not "£XXX"
    if (isUK && currentCurrency === 'GBP' && !formattedValue.includes('GBP')) {
      formattedValue += '';
    }
    
    return formattedValue;
  }

  isCanadaCountry() {
    // Check if Shopify's country data is available
    if (typeof Shopify !== 'undefined' && Shopify.country) {
      return Shopify.country === 'CA';
    }
    
    // Check for country selector element
    const countrySelector = document.querySelector('#country-selector, .country-selector');
    if (countrySelector) {
      const selectedCountry = countrySelector.value || countrySelector.textContent;
      return selectedCountry === 'CA' || selectedCountry === 'Canada' || 
             selectedCountry.includes('Canada');
    }
    
    // Check for Canadian currency or locale in URL
    const url = window.location.href;
    return url.includes('/en-ca/') || url.includes('?country=ca') || 
           url.includes('?locale=en-ca') || url.includes('currency=CAD');
  }  
  
  // Helper method to determine if the country is UK
  isUKCountry() {
    // Check if Shopify's country data is available
    if (typeof Shopify !== 'undefined' && Shopify.country) {
      return Shopify.country === 'GB';
    }
    
    // Check for country selector element
    const countrySelector = document.querySelector('#country-selector, .country-selector');
    if (countrySelector) {
      const selectedCountry = countrySelector.value || countrySelector.textContent;
      return selectedCountry === 'GB' || selectedCountry === 'UK' || 
             selectedCountry.includes('United Kingdom');
    }
    
    // Check for UK currency or locale in URL
    const url = window.location.href;
    return url.includes('/en-gb/') || url.includes('?country=gb') || 
           url.includes('?locale=en-gb') || url.includes('currency=GBP');
  }
  
  onVariantChange(radio) {
    const selectedValue = radio.value;
    const optionIndex = radio.dataset.optionIndex;
    const productId = radio.closest('[data-product-id]')?.dataset.productId;
  
    // Special handling for engraving component
    if (radio.name === 'engraving-enabled') {
      // Remove active class from all engraving buttons
      const engravingContainer = radio.closest('.addon-product-options');
      engravingContainer.querySelectorAll('.variant-button-label').forEach(label => {
        label.classList.remove('variant-button-active');
      });
      
      // Add active class to selected button
      const selectedLabel = radio.closest('.variant-button-label');
      if (selectedLabel) {
        selectedLabel.classList.add('variant-button-active');
      }
      
      // Update price display specifically for engraving selection change
      this.updateAddonPriceDisplay(radio);
      
      // Update bundle prices to reflect engraving selection
      if (window.productPriceManager) {
        window.productPriceManager.updateBundlePrices();
      }
      
      return; // Exit early since the rest is for variant options
    }
  
    if (this.primaryAddonGroup && productId === this.primaryAddonGroup.dataset.productId) {
      // Store the current main product value
      this.currentMainProduct = selectedValue.toLowerCase();
      
      // Update URL when main product variant changes
      this.updateUrlWithVariant(radio);
      
      // Check if we have a product configurator
      const hasConfigurator = window.productConfigurator && 
                            window.productConfigurator.configGroups && 
                            window.productConfigurator.configGroups.size > 0;
  
      if (radio.checked && radio.value !== 'none') {
        const variant = radio.value.toLowerCase();
        this.currentMainProduct = variant;
        
        // NEW: When variant changes, temporarily hide all image elements to prevent flickering
        document.querySelectorAll('.product__media-item').forEach(item => {
          item.style.setProperty('display', 'none', 'important');
        });
        
        // If configurator is active, wait a tiny bit then update overlays
        if (hasConfigurator && window.productConfigurator.isConfiguratorActive) {
          setTimeout(() => {
            window.productConfigurator.showBaseImage();
            window.productConfigurator.updateOverlays();
            
            // NEW: Make sure active slide is visible
            const activeSlide = document.querySelector('.product__media-item.is-active');
            if (activeSlide) {
              activeSlide.style.setProperty('display', 'block', 'important');
            }
          }, 10);
        } else {
          // For non-configurator mode or when configurator is not active
          if (hasConfigurator) {
            // Try to show config base image first
            const hasConfigImage = window.productConfigurator.showConfigBaseImage(this.currentMainProduct);
            if (!hasConfigImage) {
              // Fall back to regular variant images if no config image
              window.productConfigurator.showVariantImages(this.currentMainProduct);
            }
          } else {
            // If no configurator at all, use our own filtering method
            this.filterMainProductImages(this.currentMainProduct);
          }
          
          // Make sure active slide is visible
          setTimeout(() => {
            const activeSlide = document.querySelector('.product__media-item.is-active');
            if (activeSlide) {
              activeSlide.style.setProperty('display', 'block', 'important');
            }
          }, 50);
        }
      }
      
      if (hasConfigurator) {
        // If configurator exists, we've already handled it above
      } else {
        // If no configurator, use direct image filtering
        this.filterMainProductImages(this.currentMainProduct);
      }
    } 
  
    if (this.primaryAddonGroup && !this.primaryAddonGroup.dataset.showConfig) {    
      const productHandle = this.primaryAddonGroup.dataset.handle;
      // Instead of forcing all images to show, call toggleProductImages to filter them
      this.toggleProductImages(productHandle, true);
    }
     
    // Important: ALWAYS update the active class state, even for config products
    // Remove active class from all swatches/buttons in this option group
    const optionGroup = radio.closest('.addon-option');
    if (optionGroup) {
      // Handle swatches - always clear existing active classes first
      optionGroup.querySelectorAll('.variant-swatch-label').forEach(label => {
        label.classList.remove(PRODUCT_CONFIG.addOn.activeSwatchClass);
      });
      
      // Handle buttons - always clear existing active classes first
      optionGroup.querySelectorAll('.variant-button-label').forEach(label => {
        label.classList.remove(PRODUCT_CONFIG.addOn.activeButtonClass);
      });
      
      // Add active class to selected swatch/button
      const selectedLabel = radio.closest('.variant-swatch-label, .variant-button-label');
      if (selectedLabel) {
        if (selectedLabel.classList.contains('variant-swatch-label')) {
          selectedLabel.classList.add(PRODUCT_CONFIG.addOn.activeSwatchClass);
        } else {
          selectedLabel.classList.add(PRODUCT_CONFIG.addOn.activeButtonClass);
        }
      }
  
      // Update price display for all addon groups with the same product ID
      this.updateAddonPriceDisplay(radio);
      
      // Also update prices for other addon groups with the same product ID
      const addonGroups = document.querySelectorAll(`.addon-product-options[data-product-id="${productId}"]`);
      addonGroups.forEach(group => {
        // Find the checked radio in this group
        const checkedRadio = group.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
        if (checkedRadio && checkedRadio !== radio) {
          // Update price display for this group as well
          this.updateAddonPriceDisplay(checkedRadio);
        }
      });
    }
  
    // Calculate and log total prices
    let totalOgPrice = 0;
    let totalFinalPrice = 0;
  
    // Get all selected radios across all addon groups 
    const selectedRadios = document.querySelectorAll(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
    selectedRadios.forEach(selectedRadio => {
      if (!selectedRadio.dataset.isNone) {
        // Get original price
        const ogPrice = parseInt(selectedRadio.dataset.ogPrice || 0);
        
        // Get final price, use original price if final price is 0 or not set
        let finalPrice = parseInt(selectedRadio.dataset.finalPrice || 0);
        if (finalPrice === 0) {
          finalPrice = ogPrice;
        }
  
        // Add to totals
        totalOgPrice += ogPrice;
        totalFinalPrice += finalPrice;
      }
    });
    
    if (window.productConfigurator) {
      window.productConfigurator.updateStickyPreview();
    }
  }

  // Updated updateAddonPriceDisplay method to ensure it uses formatMoney consistently
  updateAddonPriceDisplay(radio) {
    const group = radio.closest('.addon-product-options');
    const isEngraving = group.dataset.handle === PRODUCT_CONFIG.addOn.engravingHandle;
    const isColorSwatch = group.querySelector('.variant-swatch-label') !== null;
    const noBundleDiscount = group.dataset.noBundleDiscount === 'true';
    
    // Find the variant selector container
    const variantSelectorContainer = isEngraving ? 
      group.querySelector('.flex.items-center.gap-sm') : 
      group.querySelector('.addon-option');
  
    if (!variantSelectorContainer) return;
  
    // Find or create variant name container
    let variantContainer = group.querySelector('.addon-display-container');
    if (!variantContainer) {
      variantContainer = document.createElement('div');
      variantContainer.className = 'addon-display-container flex flex-col mt-sm';
      
      // For color swatches, add the variant name display
      if (isColorSwatch) {
        const variantNameElem = document.createElement('div');
        variantNameElem.className = 'addon-variant-name text-xs text-gray-400';
        variantContainer.appendChild(variantNameElem);
      }
  
      // Insert variant container after the flex wrap container
      const optionsContainer = variantSelectorContainer.querySelector('.flex.flex-wrap.gap-sm');
      if (optionsContainer) {
        optionsContainer.insertAdjacentElement('afterend', variantContainer);
      } else {
        variantSelectorContainer.appendChild(variantContainer);
      }
    }
  
    // Find or create price display in heading
    let priceDisplay = group.querySelector('.addon-price-display');
    if (!priceDisplay) {
      priceDisplay = document.createElement('div');
      priceDisplay.className = 'addon-price-display mr-0 ml-xs';
      
      // Find the addon heading and append price display
      // Find and update addon heading
      const addonHeading = group.querySelector('.addon-heading');
      if (addonHeading) {
        addonHeading.className = 'addon-heading text-lg flex items-center font-semibold m-0 mb-md';
        addonHeading.appendChild(priceDisplay);
      }
    }
  
    // Get references to our display elements
    const variantNameElem = variantContainer.querySelector('.addon-variant-name');
  
    // Update variant name for color swatches
    if (isColorSwatch && variantNameElem) {
      const variantValue = radio.value;
      const displayValue = radio.dataset.isNone === 'true' || variantValue.toLowerCase() === 'none' 
        ? 'None' 
        : variantValue.charAt(0).toUpperCase() + variantValue.slice(1);
      variantNameElem.textContent = `${displayValue}`;
    }
  
    // Special handling for engraving
    if (isEngraving) {
      // Get the "enabled" radio button to always use its price
      const enabledRadio = group.querySelector('input[name="engraving-enabled"][value="enabled"]');
      if (!enabledRadio) return;
      
      // Get the prices directly from the "enabled" radio button's data attributes
      const originalPrice = parseInt(enabledRadio.dataset.ogPrice || 0);
      const finalPrice = parseInt(enabledRadio.dataset.finalPrice || 0);
      
      // Format prices
      const formattedOriginalPrice = this.formatMoney(originalPrice);
      const formattedFinalPrice = this.formatMoney(finalPrice || originalPrice);
      
      // Show discount only if finalPrice is less than originalPrice and noBundleDiscount is false
      if (finalPrice && finalPrice < originalPrice && !noBundleDiscount) {
        priceDisplay.innerHTML = `<s class="text-black mr-xs font-harmonia font-medium">${formattedOriginalPrice}</s><span class="text-green font-harmonia font-medium">${formattedFinalPrice}</span>`;
      } else {
        priceDisplay.innerHTML = formattedOriginalPrice;
      }
  
      return;
    }
  
    // Handle regular addon products
    if (radio.dataset.isNone === 'true' || radio.value.toLowerCase() === 'none') {
      const firstAvailableVariant = group.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:not([data-is-none="true"]):not([disabled])`);
      if (firstAvailableVariant) {
        const originalPrice = parseInt(firstAvailableVariant.dataset.ogPrice || 0);
        const finalPrice = parseInt(firstAvailableVariant.dataset.finalPrice || 0);
        
        // Format prices
        const formattedOriginalPrice = this.formatMoney(originalPrice);
        const formattedFinalPrice = this.formatMoney(finalPrice || originalPrice);
  
        // Display prices - show discounted price only if noBundleDiscount is false
        if (finalPrice && finalPrice < originalPrice && !noBundleDiscount) {
          priceDisplay.innerHTML = `<s class="text-black mr-xs font-harmonia font-medium">${formattedOriginalPrice}</s><span class="text-green font-harmonia font-medium">${formattedFinalPrice}</span>`;
        } else {
          priceDisplay.innerHTML = formattedOriginalPrice;
        }
  
        // Always show the current selection for variant name, even if it's "none"
        if (variantNameElem) {
          variantNameElem.textContent = "None";
        }
        
        return;
      }
    }
  
    // Update variant name for selected variant (for color swatches)
    if (variantNameElem) {
      const variantValue = radio.value;
      const displayValue = variantValue.charAt(0).toUpperCase() + variantValue.slice(1);
      variantNameElem.textContent = `${displayValue}`;
    }
  
    // Get prices from data attributes for regular addon
    const originalPrice = parseInt(radio.dataset.ogPrice || 0);
    const finalPrice = parseInt(radio.dataset.finalPrice || 0);
  
    // Format prices
    const formattedOriginalPrice = this.formatMoney(originalPrice);
    const formattedFinalPrice = this.formatMoney(finalPrice || originalPrice);
  
    // Display prices - show discounted price only if noBundleDiscount is false
    if (finalPrice && finalPrice < originalPrice && !noBundleDiscount) {
      priceDisplay.innerHTML = `<s class="text-black mr-xs font-harmonia font-medium">${formattedOriginalPrice}</s><span class="text-green font-harmonia font-medium">${formattedFinalPrice}</span>`;
    } else {
      priceDisplay.innerHTML = formattedOriginalPrice;
    }
  }

  findVariantId(selectedOptions, productId) {
    const variantData = this.variantDataMap.get(productId);
    if (!variantData?.variants) {
      return null;
    }

    const matchingVariant = variantData.variants.find(variant => 
      selectedOptions.every((option, index) => variant[`option${index + 1}`] === option)
    );

    const variantId = matchingVariant?.id || null;
    return variantId;
  }

  collectAddonProducts(switchedVariants = null) {
    const products = [];
    const switchedMap = new Map();
  
    if (switchedVariants) {
      switchedVariants.forEach(v => {
        const id = v.group?.dataset.productId;
        if (id) switchedMap.set(id, v);
      });
    }
  
    // Get engraving text once (only if enabled)
    const engravingEnabled = document.querySelector('input[name="engraving-enabled"][value="enabled"]')?.checked;
    const engravingText = engravingEnabled ? document.getElementById('engraving-input')?.value?.trim() : '';
  
    this.addonGroups.forEach(group => {
      const handle = group.dataset.handle;
      const productId = group.dataset.productId;
    
      // Handle engraving product FIRST
      if (handle === PRODUCT_CONFIG.addOn.engravingHandle) {
        const engravingEnabled = group.querySelector('input[value="enabled"]')?.checked;
        const engravingText = document.getElementById('engraving-input-old')?.value?.trim();
  
        const variantData = this.variantDataMap.get(productId);
        const engravingVariantId = variantData?.variants?.[0]?.id;
        
        if (engravingEnabled && engravingText) {
          if (engravingVariantId) {
            products.push({
              id: engravingVariantId,
              quantity: 1,
              properties: {
                "Engraving Text": engravingText
              }
            });
          }
        }
    
        return; // Stop processing this block, engraving is handled
      }
  
      // 🔍 Find selected variant or fallback
      const matched = switchedVariants
        ? switchedMap.get(productId)
        : group.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
  
      // Skip if "none" is selected
      if (!matched) {
        const noneSelected = Array.from(group.querySelectorAll('input[type="radio"]'))
          .some(r => r.checked && r.dataset.isNone === 'true');
        if (noneSelected) return;
      }
  
      let variantId;
  
      if (matched) {
        variantId = parseInt(switchedVariants ? matched.variantId : matched.dataset.variantId, 10);
      } else {
        const variantData = this.variantDataMap.get(productId);
        if (variantData?.variants?.length === 1) {
          variantId = variantData.variants[0].id;
        }
      }
  
      if (!variantId || isNaN(variantId)) {
        return;
      }
  
      const productData = {
        id: variantId,
        quantity: 1,
        properties: {}
      };
  
      // IMPORTANT FIX: Only add engraving text to the main product
      // Check if this is the main product (first addon group)
      const isMainProduct = group === this.primaryAddonGroup;
      if (isMainProduct && engravingEnabled && engravingText) {
        productData.properties["Engraving Text"] = engravingText;
      }
  
      // ⛓️ Add selling plan if available
      const sellingPlanInput = group.querySelector(PRODUCT_CONFIG.addOn.sellingPlanInput);
      if (sellingPlanInput?.value) {
        const sellingPlanId = parseInt(sellingPlanInput.value, 10);
        if (!isNaN(sellingPlanId)) {
          productData.selling_plan = sellingPlanId;
        }
      }
  
      products.push(productData);
    });
  
    return products;
  }

  getPrimaryProductData() {
    if (!this.primaryAddonGroup) return null;
    
    const productId = this.primaryAddonGroup.dataset.productId;
    const variantData = this.variantDataMap.get(productId);
    
    // Get selected variant
    const selectedRadio = this.primaryAddonGroup.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
    if (!selectedRadio) return null;
    
    // Get variant ID
    let variantId;
    if (!variantData?.variants || variantData.variants.length === 1) {
      variantId = variantData?.variants?.[0]?.id || productId;
    } else {
      const selectedOptions = [selectedRadio.value];
      variantId = this.findVariantId(selectedOptions, productId);
    }
    
    // Base product data
    const productData = {
      id: parseInt(variantId, 10),
      quantity: 1,
      properties: {}
    };
    
    // Add engraving if enabled
    const engravingEnabled = document.querySelector('input[name="engraving-enabled"]:checked')?.value === 'enabled';
    const engravingText = document.getElementById('engraving-input')?.value?.trim();
    
    if (engravingEnabled && engravingText) {
      productData.properties = {
        [PRODUCT_CONFIG.addOn.engravingPropertyKey]: engravingText
      };
    }
    
    // Add selling plan if it exists
    const sellingPlanInput = this.primaryAddonGroup.querySelector('input[name="selling_plan"].addon-selling-plan');
    if (sellingPlanInput?.value) {
      const sellingPlanId = parseInt(sellingPlanInput.value, 10);
      if (!isNaN(sellingPlanId)) {
        productData.selling_plan = sellingPlanId;
      }
    }
    
    return productData;
  }

  async addProductsToCart() {
    try {
      const cor502SwitchEnabled = document.querySelector('[data-enable-cor502-switch]')?.dataset.enableCor502Switch === 'true';
    
      const selectedVariants = [];
  
      this.addonGroups.forEach(group => {
        const radio = group.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
        if (!radio || radio.dataset.isNone === 'true') return;
  
        selectedVariants.push({
          handle: group.dataset.handle,
          group,
          variantId: radio.dataset.variantId,
          mainSwitch: radio.dataset.variantMainProductSwitch,
          caseSwitch: radio.dataset.variantFreeProductSwitch,
          radio
        });
      });
  
      if (cor502SwitchEnabled) {  
        const caseEntry = selectedVariants.find(p => p.handle === 'core-500-stethoscope-case');
        const switchCandidate = selectedVariants.find(p => p.mainSwitch && p.caseSwitch);
  
        if (caseEntry && switchCandidate) {
          switchCandidate.variantId = switchCandidate.mainSwitch;
          caseEntry.variantId = switchCandidate.caseSwitch;
        }
      }
  
      const products = this.collectAddonProducts(selectedVariants).reverse();

      const cartData = { items: products };
  
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartData)
      });
  
      if (!response.ok) throw new Error(await response.text());
  
      if (this.isDrawerCart) {
        await this.updateCartDrawer();
      } else {
        window.location.href = '/cart';
      }
  
    } catch (error) {
      console.error('Add to Cart Error:', error);
      this.showErrorNotification(error);
      throw error;
    }
  }

  updateCartCount(count) {
    const cartCount = document.querySelector('.cart-count-bubble');
    if (cartCount) {
      const countSpan = cartCount.querySelector('span');
      if (countSpan) {
        countSpan.textContent = count;
        // Make sure the bubble is visible
        cartCount.style.display = count > 0 ? 'flex' : 'none';
      }
    }
  }

  async updateCartDrawer() {
    if (!this.cartDrawer) {
      return;
    }

    try {
      // Fetch the cart drawer section HTML
      const response = await fetch(`?section_id=cart-drawer`);
      if (!response.ok) throw new Error('Failed to fetch cart drawer content');
      
      const html = await response.text();
      
      // Create a temporary container and parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawerContent = doc.querySelector('.drawer__inner');
      
      if (!newDrawerContent) {
        throw new Error('Could not find drawer content in response');
      }

      // Update the drawer content
      const currentDrawerContent = this.cartDrawer.querySelector('.drawer__inner');
      if (currentDrawerContent) {
        currentDrawerContent.innerHTML = newDrawerContent.innerHTML;
      }

      // Remove is-empty class from cart drawer
      this.cartDrawer.classList.remove('is-empty');
      
      // Also remove is-empty from drawer__inner if it exists
      if (currentDrawerContent) {
        currentDrawerContent.classList.remove('is-empty');
      }

      // Open the drawer
      if (typeof this.cartDrawer.open === 'function') {
        // Give the DOM time to update before opening
        setTimeout(() => {
          // Double-check and remove is-empty class again after the timeout
          this.cartDrawer.classList.remove('is-empty');
          if (currentDrawerContent) {
            currentDrawerContent.classList.remove('is-empty');
          }
          this.cartDrawer.open();
        }, 100);
      }

    } catch (error) {
      // Even if we fail to update the drawer content, try to open it
      if (typeof this.cartDrawer.open === 'function') {
        // Remove is-empty class even in error case if we know items were added
        this.cartDrawer.classList.remove('is-empty');
        const currentDrawerContent = this.cartDrawer.querySelector('.drawer__inner');
        if (currentDrawerContent) {
          currentDrawerContent.classList.remove('is-empty');
        }
        this.cartDrawer.open();
      }
    }
  }

  showErrorNotification(error) {
      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification) {
        const errorMessageElement = cartNotification.querySelector('.cart-notification__error');
        if (errorMessageElement) {
          errorMessageElement.textContent = error.message;
          cartNotification.classList.remove('animate', 'hidden');
          cartNotification.classList.add('animate', 'active');
        }
      }
    }
  }



// Membership products
class AddOnMembershipHandler {
  constructor() {
    // Looking specifically for the Eko+ membership container
    this.membershipContainer = document.querySelector('.addon-product-options[data-handle="annual-membership-free-trial"]');
    if (!this.membershipContainer) {
      return;
    }
    
    this.init();
  }

  init() {
    // Find the add to cart button - being more specific with selector
    const addButton = this.membershipContainer.querySelector('.button');
    if (!addButton) {
      return;
    }

    // Add click handler
    addButton.addEventListener('click', (e) => this.handleAddToCart(e));
  }

  async handleAddToCart(event) {
    event.preventDefault();
    const button = event.currentTarget;
    
    // Get product ID and selling plan from container
    const productId = this.membershipContainer.dataset.productId;
    const sellingPlanInput = this.membershipContainer.querySelector('input[name="selling_plan"]');
    const sellingPlan = sellingPlanInput?.value;

    if (!productId || !sellingPlan) {
      console.error('Missing required data:', { productId, sellingPlan });
      return;
    }

    // Show loading state
    const originalText = button.textContent;
    button.classList.add('is-loading');
    button.textContent = 'Adding...';
    button.disabled = true;

    try {
      // Ensure values are properly formatted
      const cartItem = {
        id: parseInt(productId, 10),
        quantity: 1,
        selling_plan: parseInt(sellingPlan, 10)
      };

      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: [cartItem] })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }

      const result = await response.json();

      // On success, redirect to cart page
      window.location.href = '/cart';

    } catch (error) {
      console.error('Add to Cart Error:', error);
      button.textContent = 'Error - Try Again';
      setTimeout(() => {
        button.textContent = originalText;
      }, 3000);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      if (button.textContent === 'Adding...') {
        button.textContent = originalText;
      }
    }
  }
}

// For ProductPriceManager class
class ProductPriceManager {
  constructor() {
    this.stickyPriceDisplay = document.querySelector('.pdp-price-sticky');
    this.stickyPriceCompare = document.querySelector('.pdp-bundle-price-compare');
    this.stickyPriceFinal = document.querySelector('.pdp-bundle-price-final');
    this.addonGroups = document.querySelectorAll(PRODUCT_CONFIG.addOn.addonGroupSelector);
    this.variantDataMap = new Map();
    
    this.init();
  }

  init() {
    // Load variant data for each add-on product
    const variantDataElements = document.querySelectorAll('[id^="addon-variant-data-"]');
    variantDataElements.forEach(element => {
      try {
        const variantData = JSON.parse(element.textContent);
        const productId = element.id.replace('addon-variant-data-', '');
        this.variantDataMap.set(productId, variantData);
      } catch (e) {
        console.error('Error parsing variant data:', e);
      }
    });

    // Listen for variant changes on main product and add-ons
    if (this.mainProductForm) {
      const mainProductVariantSelect = this.mainProductForm.querySelector('select[name="id"]');
      if (mainProductVariantSelect) {
        mainProductVariantSelect.addEventListener('change', () => this.updateBundlePrices());
      }
    }

    // Listen for add-on variant changes
    this.addonGroups.forEach(group => {
      const radios = group.querySelectorAll(PRODUCT_CONFIG.addOn.addonVariantSelector);
      radios.forEach(radio => {
        radio.addEventListener('change', () => this.updateBundlePrices());
      });
    });

    // Initial price update
    this.updateBundlePrices();
  }

  getMainProductPrice() {
    try {
      const variantId = this.mainProductForm?.querySelector('input[name="id"]')?.value;
      if (!variantId) return { regularPrice: 0, discountPrice: 0 };

      // Get the variant data from the product JSON
      const productSection = this.mainProductForm.closest('[data-section]');
      const sectionId = productSection?.dataset.section;
      const productJsonElement = document.getElementById(`ProductJson-${sectionId}`);
      
      if (productJsonElement) {
        const productData = JSON.parse(productJsonElement.textContent);
        const variant = productData.variants.find(v => v.id.toString() === variantId.toString());
        
        if (variant) {
          const regularPrice = variant.price;
          let discountPrice = null;

          // Check for metafield discount price
          if (variant.metafields?.custom?.variant_bundle_discount_price) {
            discountPrice = Math.round(parseFloat(variant.metafields.custom.variant_bundle_discount_price) * 100);
          }

          return {
            regularPrice: regularPrice,
            discountPrice: discountPrice || regularPrice
          };
        }
      }

      // Fallback to price elements if JSON not available
      const regularPriceElement = document.querySelector('.price__regular .price-item--regular, .price__sale .price-item--regular');
      const salePriceElement = document.querySelector('.price__sale .price-item--sale');
      
      const regularPrice = regularPriceElement ? this.extractPriceValue(regularPriceElement.textContent) : 0;
      const salePrice = salePriceElement ? this.extractPriceValue(salePriceElement.textContent) : regularPrice;

      return {
        regularPrice: regularPrice,
        discountPrice: salePrice
      };

    } catch (error) {
      console.error('Error getting main product price:', error);
      return { regularPrice: 0, discountPrice: 0 };
    }
  }

  getSelectedAddOnPrices() {
    let totalRegularPrice = 0;
    let totalDiscountPrice = 0;

    this.addonGroups.forEach(group => {
      const selectedRadio = group.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
      if (!selectedRadio || selectedRadio.dataset.isNone === 'true' || selectedRadio.value.toLowerCase() === 'none') {
        return;
      }

      const productId = group.dataset.productId;
      const variantData = this.variantDataMap.get(productId);
      
      if (!variantData?.variants) return;

      // Get selected variant
      const selectedOptions = Array.from(group.querySelectorAll(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`))
        .map(radio => radio.value);

      let variant = variantData.variants.length === 1 
        ? variantData.variants[0]
        : variantData.variants.find(v => 
            selectedOptions.every((option, index) => v[`option${index + 1}`] === option)
          );

      if (variant) {
        const regularPrice = variant.price;
        let discountPrice = null;

        // Check for metafield discount price
        if (variant.metafields?.custom?.variant_bundle_discount_price) {
          discountPrice = Math.round(parseFloat(variant.metafields.custom.variant_bundle_discount_price) * 100);
        }

        totalRegularPrice += regularPrice;
        totalDiscountPrice += discountPrice || regularPrice;
      }
    });

    return {
      regularPrice: totalRegularPrice,
      discountPrice: totalDiscountPrice
    };
  }

  // Updated updateBundlePrices method to handle no_bundle_discount
  updateBundlePrices() {
    try {
      // Start with add-on calculations (the working part)
      let totalOgPrice = 0;
      let totalFinalPrice = 0;
      let hasAnyNoBundleDiscount = false;
  
      // Check if any addon has no_bundle_discount set to true
      const addonGroups = document.querySelectorAll('.addon-product-options');
      addonGroups.forEach(group => {
        if (group.dataset.noBundleDiscount === 'true') {
          hasAnyNoBundleDiscount = true;
        }
      });
  
      const selectedRadios = document.querySelectorAll(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
      selectedRadios.forEach(selectedRadio => {
        if (!selectedRadio.dataset.isNone) {
          const addonGroup = selectedRadio.closest('.addon-product-options');
          const noBundleDiscount = addonGroup && addonGroup.dataset.noBundleDiscount === 'true';
          
          const ogPrice = parseInt(selectedRadio.dataset.ogPrice || 0);
          let finalPrice = parseInt(selectedRadio.dataset.finalPrice || 0);
          
          if (finalPrice === 0 || noBundleDiscount) {
            finalPrice = ogPrice;
          }
          
          totalOgPrice += ogPrice;
          totalFinalPrice += finalPrice;
        }
      });
  
      // Special handling for engraving product
      const engravingEnabled = document.querySelector('input[name="engraving-enabled"][value="enabled"]:checked');
      const engravingText = document.getElementById('engraving-input')?.value?.trim() || 
                            document.getElementById('engraving-input-old')?.value?.trim();
      
      if (engravingEnabled && engravingText) {
        const engravingGroup = document.querySelector(`.addon-product-options[data-handle="${PRODUCT_CONFIG.addOn.engravingHandle}"]`);
        if (engravingGroup) {
          const noBundleDiscount = engravingGroup.dataset.noBundleDiscount === 'true';
          
          // Check if this engraving product is already included in the selected radios
          const isEngravingInSelectedRadios = Array.from(selectedRadios).some(selected => {
            const selectedGroup = selected.closest('.addon-product-options');
            return selectedGroup && selectedGroup.dataset.handle === PRODUCT_CONFIG.addOn.engravingHandle;
          });
          
          // Only add to totals if not already counted
          if (!isEngravingInSelectedRadios) {
            // Get prices directly from the engraving radio
            const engravingRadio = engravingEnabled;
            const originalPrice = parseInt(engravingRadio.dataset.ogPrice || 0);
            let finalPrice = parseInt(engravingRadio.dataset.finalPrice || 0);
            
            // If finalPrice is not set or is zero, or if noBundleDiscount is true, use original price
            if (!finalPrice || noBundleDiscount) {
              finalPrice = originalPrice;
            }
            
            totalOgPrice += originalPrice;
            totalFinalPrice += finalPrice;
          }
        }
      }
  
      // Get main product price
      const mainProduct = this.getMainProductPrice();
      
      // Calculate bundle totals
      const bundleOgPrice = totalOgPrice + mainProduct.regularPrice;
      const bundleFinalPrice = hasAnyNoBundleDiscount ? bundleOgPrice : (totalFinalPrice + mainProduct.discountPrice);
  
      // Update price display elements
      if (this.stickyPriceCompare) {
        this.stickyPriceCompare.textContent = this.formatMoney(bundleOgPrice);
        // Only show compare price if we have a discount and no addon has noBundleDiscount
        this.stickyPriceCompare.style.display = (bundleOgPrice > bundleFinalPrice && !hasAnyNoBundleDiscount) ? 'inline' : 'none';
      }
  
      if (this.stickyPriceFinal) {
        this.stickyPriceFinal.textContent = this.formatMoney(bundleFinalPrice);
      }
  
      // Update savings display - only show if we have a discount and no addon has noBundleDiscount
      const savingsElement = document.querySelector('.pdp-bundle-savings');
      if (savingsElement) {
        const savings = bundleOgPrice - bundleFinalPrice;
        if (savings > 0 && !hasAnyNoBundleDiscount) {
          savingsElement.textContent = `${this.formatMoney(savings)} OFF`;
          savingsElement.style.display = 'inline-block';
        } else {
          savingsElement.textContent = '';
          savingsElement.style.display = 'none';
        }
      }      
  
    } catch (error) {
      console.error('Error updating bundle prices:', error);
    }
  }

  extractPriceValue(priceString) {
    try {
      const numericString = priceString.replace(/[^0-9.]/g, '');
      return Math.round(parseFloat(numericString) * 100);
    } catch (error) {
      console.error('Error extracting price value:', error);
      return 0;
    }
  }

  // Helper methods for currency detection
  isUKCountry() {
    // Check if Shopify's country data is available
    if (typeof Shopify !== 'undefined' && Shopify.country) {
      return Shopify.country === 'GB';
    }
    
    // Check for country selector element
    const countrySelector = document.querySelector('#country-selector, .country-selector');
    if (countrySelector) {
      const selectedCountry = countrySelector.value || countrySelector.textContent;
      return selectedCountry === 'GB' || selectedCountry === 'UK' || 
             selectedCountry.includes('United Kingdom');
    }
    
    // Check for UK currency or locale in URL
    const url = window.location.href;
    return url.includes('/en-gb/') || url.includes('?country=gb') || 
           url.includes('?locale=en-gb') || url.includes('currency=GBP');
  }
  
  isCanadaCountry() {
    // Check if Shopify's country data is available
    if (typeof Shopify !== 'undefined' && Shopify.country) {
      return Shopify.country === 'CA';
    }
    
    // Check for country selector element
    const countrySelector = document.querySelector('#country-selector, .country-selector');
    if (countrySelector) {
      const selectedCountry = countrySelector.value || countrySelector.textContent;
      return selectedCountry === 'CA' || selectedCountry === 'Canada' || 
             selectedCountry.includes('Canada');
    }
    
    // Check for Canadian currency or locale in URL
    const url = window.location.href;
    return url.includes('/en-ca/') || url.includes('?country=ca') || 
           url.includes('?locale=en-ca') || url.includes('currency=CAD');
  }

  // Updated formatMoney method
  formatMoney(cents) {
    // Check if we're in the UK or Canada
    const isUK = this.isUKCountry();
    const isCanada = this.isCanadaCountry();
    
    // Get current Shopify currency or use appropriate fallback
    const currentCurrency = Shopify?.currency?.active || (isUK ? 'GBP' : (isCanada ? 'CAD' : 'USD'));
  
    let formattedValue;
    
    // If Shopify's formatMoney is available, use it first
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      formattedValue = Shopify.formatMoney(cents);
    } else {
      // Fallback to browser's Intl formatter with correct locale and currency
      formattedValue = new Intl.NumberFormat(isUK ? 'en-GB' : (isCanada ? 'en-CA' : undefined), {
        style: 'currency',
        currency: currentCurrency
      }).format(cents/100);
    }
    
    // Remove trailing .0 or .00
    formattedValue = formattedValue.replace(/\.0{1,2}(?=\s*$|$)/, '');
    
    // Special formatting for CAD - ensure it shows as "$XXX CAD" not "CA$XXX"
    if (isCanada && currentCurrency === 'CAD' && !formattedValue.includes('CAD')) {
      formattedValue = formattedValue.replace(/^CA\$/, '$');
      formattedValue += ' CAD';
    }
    
    // Special formatting for GBP - ensure it shows as "£XXX GBP" not "£XXX"
    if (isUK && currentCurrency === 'GBP' && !formattedValue.includes('GBP')) {
      formattedValue += ' GBP';
    }
    
    return formattedValue;
  }
}

// Enhanced Product Configurator that uses direct overlays on base image and thumbnails
class ProductConfigurator {
  constructor() {
    // Configurator state
    this.isConfiguratorActive = false;
    this.mediaItems = [];
    this.thumbnailItems = [];
    this.baseImages = {}; // Main slides
    this.baseThumbnails = {}; // Corresponding thumbnails
    this.regularImages = {};
    this.overlayImages = {
      eartips: {},
      chestpiece: {}
    };
    this.configGroups = new Map();
    this.currentMainProduct = null;
    this.currentEartips = null;
    this.currentChestpiece = null;
    this.lastEartipsValue = null;
    this.lastChestpieceValue = null;
    this.isOverlayActive = false;
    this.activeOverlays = [];
    this.activeThumbnailOverlays = [];
    
    // Find image containers
    this.galleryContainer = document.querySelector('.product__media-wrapper');
    this.mediaList = document.querySelector('.product__media-list');
    this.thumbnailList = document.querySelector('.thumbnail-list');

    this.stickyPreview = document.querySelector('.pdp-mobile-sticky-preview');
    this.stickyPreviewImage = this.stickyPreview ? this.stickyPreview.querySelector('.pdp-sticky-preview-image') : null;
    this.variantLabel = this.stickyPreview ? this.stickyPreview.querySelector('.variant-label') : null;
    this.scrollThreshold = 0;
    this.isStickyVisible = false;
    this.scrollDebounceTimer = null;
    
    this.init();
  }
  
  // Updated init method for ProductConfigurator class
  init() {
    // Process configurator attributes for all addon groups
    this.processConfigGroups();
    
    // Initialize sticky preview regardless of configurator groups
    if (this.stickyPreview && this.galleryContainer) {
      // Set initial display to block but keep it offscreen
      this.stickyPreview.style.display = 'block';
      
      // Calculate initial scroll threshold
      this.updateScrollThreshold();
      
      // Setup scroll event listener
      window.addEventListener('scroll', this.handleStickyScroll.bind(this));
      window.addEventListener('resize', this.handleStickyResize.bind(this));
      
      // Setup click to open overlay
      this.stickyPreview.addEventListener('click', this.handleStickyClick.bind(this));
      
      // Initial update of preview
      this.updateStickyPreview();
    }
    
    // Only proceed with other configurator initialization if groups exist
    if (this.configGroups.size === 0) {
      return;
    }
    
    // Map all gallery and thumbnail images to find configurator-compatible ones
    this.mapGalleryImages();
    this.mapThumbnails();
    
    // Add event listeners to addon variant radios to trigger configurator
    this.addVariantListeners();
    
    // Preload configurator images
    this.preloadImages();
    
    // Store original gallery state
    this.storeOriginalGalleryState();
  
    if (this.currentMainProduct) {
      this.showConfigBaseImage(this.currentMainProduct);
    }
  
    // Add this event listener to track clicking on thumbnails
    document.addEventListener('click', (e) => {
      const clickedThumbnail = e.target.closest('.thumbnail-list__item');
      if (clickedThumbnail && this.isConfiguratorActive) {
        // Force refresh all addon config images after a short delay
        setTimeout(() => {
          // This will ensure all types of config images remain visible
          this.preserveAddonConfigImages();
        }, 50);
      }
    }, true);    
  }
  
  storeOriginalGalleryState() {
    // Store references to original gallery elements for restoration
    this.originalGalleryImages = Array.from(this.mediaItems).map(item => ({
      element: item,
      display: item.style.display || '',
      isActive: item.classList.contains('is-active'),
      mediaId: item.dataset.mediaId
    }));
    
    // Store references to original thumbnail elements
    this.originalThumbnails = Array.from(this.thumbnailItems).map(item => ({
      element: item,
      display: item.style.display || '',
      isActive: item.classList.contains('is-active'),
      target: item.dataset.target
    }));

  }
  
  processConfigGroups() {
    // Find all addon product groups
    const addonGroups = document.querySelectorAll(PRODUCT_CONFIG.addOn.addonGroupSelector);
    
    // Process each addon group
    addonGroups.forEach((group, index) => {
      // Check if this group has configurator enabled via data attribute
      const showConfig = group.dataset.showConfig === 'true';
      const configType = group.dataset.configType || null;
      
      if (showConfig) {
        // Store in configGroups map for reference
        this.configGroups.set(group.dataset.productId || `group-${index}`, {
          element: group,
          type: configType,
          handle: group.dataset.handle || null
        });
        
      }
      
      // The first addon group is always the main product
      if (index === 0) {
        this.mainProductGroup = group;
        
        // Set initial main product value 
        const checkedRadio = group.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
        if (checkedRadio && checkedRadio.value !== 'none') {
          this.currentMainProduct = checkedRadio.value.toLowerCase();
        }
      }
    });
  }
  
  mapGalleryImages() {
    // Find all product media items
    this.mediaItems = document.querySelectorAll('.product__media-item');
    
    if (!this.mediaItems.length) {
      return;
    }
    
    // First, categorize all images by variant
    this.mediaItems.forEach(item => {
      // Store original display style for later restoration
      item.dataset.originalDisplay = item.style.display || '';
      
      const media = item.querySelector('img');
      if (!media) return;
      
      const altText = media.alt || '';
      const src = media.src || media.dataset.src;
      
      // Skip if no alt text or src
      if (!altText || !src) return;
      
      // Check if this is a configurator image
      const isMainImage = altText.includes('for config_main');
      const isEartipsImage = altText.includes('for config_eartips');
      const isChestpieceImage = altText.includes('for config_chestpiece');
      
      // Handle configurator overlay images (eartips, chestpiece)
      if (isEartipsImage || isChestpieceImage) {
        if (altText.includes('Color:')) {
          const colorMatch = altText.match(/Color:\s*(.*?)\s+for\s+config/i);
          if (colorMatch && colorMatch[1]) {
            const colorName = colorMatch[1].trim().toLowerCase();
            
            if (isEartipsImage) {
              this.overlayImages.eartips[colorName] = {
                mediaItem: item,
                src: src,
                altText: altText
              };
            } else if (isChestpieceImage) {
              this.overlayImages.chestpiece[colorName] = {
                mediaItem: item,
                src: src,
                altText: altText
              };
            }
          }
        }
        return;
      }
      
      // Process main product configurator base images
      if (isMainImage && altText.includes('Color:')) {
        const colorMatch = altText.match(/Color:\s*(.*?)\s+for\s+config/i);
        if (colorMatch && colorMatch[1]) {
          const colorName = colorMatch[1].trim().toLowerCase();
          
          this.baseImages[colorName] = {
            mediaItem: item,
            src: src,
            altText: altText,
            mediaId: item.dataset.mediaId
          };
        }
        return;
      }
      
      // Process regular variant images (not configurator specific)
      if (altText.includes('Color:') && !altText.includes('for config_')) {
        const colorMatch = altText.match(/Color:\s*([^for]+)/i);
        if (colorMatch && colorMatch[1]) {
          const colorName = colorMatch[1].trim().toLowerCase();
          
          if (!this.regularImages[colorName]) {
            this.regularImages[colorName] = [];
          }
          
          this.regularImages[colorName].push({
            mediaItem: item,
            src: src,
            altText: altText,
            mediaId: item.dataset.mediaId
          });
        }
      }
    });

  }

  mapThumbnails() {
    // Find all thumbnails
    this.thumbnailItems = document.querySelectorAll('.thumbnail-list__item');
    
    if (!this.thumbnailItems.length) {
      return;
    }
    
    // Store original display styles and map thumbnails to their targets
    this.thumbnailItems.forEach(thumb => {
      thumb.dataset.originalDisplay = thumb.style.display || '';
      const targetId = thumb.dataset.target;
      
      // Find the corresponding baseImage by mediaId
      for (const variant in this.baseImages) {
        if (this.baseImages[variant].mediaId === targetId) {
          // Store thumbnail reference with the same variant key
          this.baseThumbnails[variant] = {
            thumbnailItem: thumb,
            targetId: targetId
          };
        }
      }
    });
  }

  initStickyPreview() {
    if (!this.stickyPreview || !this.galleryContainer) {
      return;
    }
    
    // Set initial display to block but keep it offscreen
    this.stickyPreview.style.display = 'block';
    
    // Calculate initial scroll threshold
    this.updateScrollThreshold();
    
    // Setup scroll event listener
    window.addEventListener('scroll', this.handleStickyScroll.bind(this));
    window.addEventListener('resize', this.handleStickyResize.bind(this));
    
    // Setup click to open overlay
    this.stickyPreview.addEventListener('click', this.handleStickyClick.bind(this));
    
    // Initial update of preview
    this.updateStickyPreview();
    
  }

  capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
  
  updateScrollThreshold() {
    if (this.galleryContainer) {
      const rect = this.galleryContainer.getBoundingClientRect();
      this.scrollThreshold = window.pageYOffset + rect.bottom;
    }
  }
  
  handleStickyScroll() {
    // Skip if not mobile
    if (window.innerWidth >= 900) return;
    
    // Debounce scroll handling for performance
    if (this.scrollDebounceTimer) {
      clearTimeout(this.scrollDebounceTimer);
    }
    
    this.scrollDebounceTimer = setTimeout(() => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Find the sticky ATC button
      const stickyAtc = document.querySelector('.pdp-sticky-atc');
      
      if (stickyAtc) {
        // Get sticky ATC position
        const atcRect = stickyAtc.getBoundingClientRect();
        const viewportMidpoint = window.innerHeight / 2;
        
        // Check if sticky ATC has reached the midpoint of the viewport
        const atcReachedMidpoint = atcRect.top <= viewportMidpoint;
        
        // Hide preview when sticky ATC reaches midpoint
        if (atcReachedMidpoint && this.isStickyVisible) {
          this.hideStickyPreview();
        } 
        // Show preview when scrolling back up past the sticky ATC
        else if (!atcReachedMidpoint && scrollTop > this.scrollThreshold && !this.isStickyVisible) {
          this.showStickyPreview();
        }
        // Original behavior: hide when scrolled back to gallery
        else if (scrollTop <= this.scrollThreshold && this.isStickyVisible) {
          this.hideStickyPreview();
        }
      } else {
        // Fallback to original behavior if sticky ATC isn't found
        if (scrollTop > this.scrollThreshold && !this.isStickyVisible) {
          this.showStickyPreview();
        } 
        else if (scrollTop <= this.scrollThreshold && this.isStickyVisible) {
          this.hideStickyPreview();
        }
      }
    }, 10);
  }
  
  handleStickyResize() {
    // Update scroll threshold
    this.updateScrollThreshold();
    
    // Hide preview if not mobile
    if (window.innerWidth >= 900 && this.isStickyVisible) {
      this.hideStickyPreview();
    }
  }
  
  showStickyPreview() {
    if (!this.isStickyVisible && this.stickyPreview) {
      this.stickyPreview.classList.add('visible');
      this.isStickyVisible = true;
    }
  }
  
  hideStickyPreview() {
    if (this.isStickyVisible && this.stickyPreview) {
      this.stickyPreview.classList.remove('visible');
      this.isStickyVisible = false;
    }
  }
  
  // Find the updateStickyPreview method in the ProductConfigurator class and replace it with this fixed version
  updateStickyPreview() {
    if (!this.stickyPreviewImage) return;
    
    // First, clear any existing content
    this.stickyPreviewImage.innerHTML = '';
    
    // For the sticky preview, we need the main product base image, not any addon images
    // Find the main product base image or a suitable fallback
    let mainImage = null;
    
    // First try to find the config base image for the current main product
    if (this.currentMainProduct) {
      const baseImageData = this.findBaseImageForVariant(this.currentMainProduct);
      if (baseImageData && baseImageData.mediaItem) {
        mainImage = baseImageData.mediaItem.querySelector('img');
      }
    }
    
    // If we couldn't find a config base image, use the active image (only if it's not an addon image)
    if (!mainImage) {
      const activeMediaItem = document.querySelector('.product__media-item.is-active');
      if (activeMediaItem) {
        const activeImg = activeMediaItem.querySelector('img');
        if (activeImg && activeImg.alt) {
          // Skip if this is an addon image with handle: in the alt text
          const altText = activeImg.alt;
          if (!altText.includes('handle:') && !altText.includes('handle: ')) {
            mainImage = activeImg;
          }
        }
      }
    }
    
    // If we still don't have an image, find any main product image
    if (!mainImage) {
      const mediaItems = document.querySelectorAll('.product__media-item');
      for (const item of mediaItems) {
        const img = item.querySelector('img');
        if (img && img.alt && !img.alt.includes('handle:') && !img.alt.includes('handle: ')) {
          mainImage = img;
          break;
        }
      }
    }
    
    // Now create the sticky preview with just the main product and configurator overlays
    if (mainImage) {
      const clonedImg = mainImage.cloneNode(true);
      this.stickyPreviewImage.appendChild(clonedImg);
      
      // Update variant text if available
      if (this.variantLabel && this.currentMainProduct) {
        this.variantLabel.textContent = this.capitalizeFirstLetter(this.currentMainProduct);
      }
      
      // Add configurator overlays but NEVER addon product images
      if (this.currentEartips) {
        this.addStickyOverlay('eartips', this.currentEartips);
      }
      
      if (this.currentChestpiece) {
        this.addStickyOverlay('chestpiece', this.currentChestpiece);
      }
    }
  }
  
  addStickyOverlay(overlayType, colorName) {
    if (!this.stickyPreviewImage) return;
    
    // Find the overlay image data
    const overlayData = this.findOverlayImage(colorName, overlayType);
    if (!overlayData) return;
    
    // Create overlay element
    const overlay = document.createElement('div');
    overlay.className = `sticky-config-overlay ${overlayType}-overlay`;
    overlay.dataset.overlayType = overlayType;
    overlay.dataset.color = colorName;
    
    // Position the overlay absolutely
    overlay.style.cssText = `
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 5;
    `;
    
    // Create image element for overlay
    const img = document.createElement('img');
    img.className = 'sticky-configurator-overlay-img';
    img.src = overlayData.src;
    img.alt = `${overlayType} overlay - ${colorName}`;
    img.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: contain;
      pointer-events: none;
    `;
    
    overlay.appendChild(img);
    this.stickyPreviewImage.appendChild(overlay);
  }
  
  handleStickyClick() {
    // If gallery overlay exists, open it
    if (window.galleryOverlay && typeof window.galleryOverlay.openOverlay === 'function') {
      window.galleryOverlay.openOverlay();
    } 
    // Fallback: click the active media item to trigger the overlay
    else {
      const activeMediaItem = document.querySelector('.product__media-item.is-active');
      if (activeMediaItem) {
        activeMediaItem.click();
      }
    }
  }  
  
  addVariantListeners() {
    // Add listeners to all addon groups with configurator enabled
    this.configGroups.forEach((group, productId) => {
      const radios = group.element.querySelectorAll(PRODUCT_CONFIG.addOn.addonVariantSelector);
      
      radios.forEach(radio => {
        radio.addEventListener('change', () => {
          // When a radio button changes in a configurator-enabled addon
          if (radio.checked) {
            const isNone = radio.dataset.isNone === 'true' || radio.value.toLowerCase() === 'none';
            
            if (!isNone) {
              // Get the selected option value directly from the radio or its label
              let selectedValue = radio.value;
              const label = radio.closest('.variant-swatch-label, .variant-button-label');
              if (label && label.dataset.optionValue) {
                // If there's a data-option-value attribute on the label, use that instead
                selectedValue = label.dataset.optionValue;
              }
                            
              // Update current component selection based on config type
              if (group.type === 'config_eartips') {
                this.currentEartips = selectedValue;
                this.activateConfigurator();
                
                // Show images for this config type and keep any other visible ones
                this.hideMatchingConfigImages('config_eartips'); // First hide any previous selection
                this.showMatchingConfigImages('config_eartips', selectedValue);
                
              } else if (group.type === 'config_chestpiece') {
                this.currentChestpiece = selectedValue;
                this.activateConfigurator();
                
                // Show images for this config type and keep any other visible ones
                this.hideMatchingConfigImages('config_chestpiece'); // First hide any previous selection
                this.showMatchingConfigImages('config_chestpiece', selectedValue);
              }
            } else {
              // Reset if 'none' selected
              if (group.type === 'config_eartips') {
                this.currentEartips = null;
                this.hideMatchingConfigImages('config_eartips');
                this.updateOverlays();
              } else if (group.type === 'config_chestpiece') {
                this.currentChestpiece = null;
                this.hideMatchingConfigImages('config_chestpiece');
                this.updateOverlays();
              }
              
              // If both eartips and chestpiece are null, deactivate configurator
              if (!this.currentEartips && !this.currentChestpiece) {
                this.deactivateConfigurator();
                
                // After deactivating configurator, show the config base image
                if (this.currentMainProduct) {
                  this.showConfigBaseImage(this.currentMainProduct);
                }
              }
            }
          }
        });
      });
      
      // Set initial values if checked
      const checkedRadio = group.element.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
      if (checkedRadio && checkedRadio.value !== 'none' && !checkedRadio.dataset.isNone) {
        // Get the selected option value directly from the radio or its label
        let selectedValue = checkedRadio.value;
        const label = checkedRadio.closest('.variant-swatch-label, .variant-button-label');
        if (label && label.dataset.optionValue) {
          // If there's a data-option-value attribute on the label, use that instead
          selectedValue = label.dataset.optionValue;
        }
                
        if (group.type === 'config_eartips') {
          this.currentEartips = selectedValue;
          setTimeout(() => {
            // First hide any other images for this config type
            this.hideMatchingConfigImages('config_eartips');
            // Then show the selected ones
            this.showMatchingConfigImages('config_eartips', selectedValue);
          }, 100);
        } else if (group.type === 'config_chestpiece') {
          this.currentChestpiece = selectedValue;
          setTimeout(() => {
            // First hide any other images for this config type
            this.hideMatchingConfigImages('config_chestpiece');
            // Then show the selected ones
            this.showMatchingConfigImages('config_chestpiece', selectedValue);
          }, 100);
        }
      }
    });
    
    // Add this document-level event listener to handle all swatch clicks correctly
    document.addEventListener('click', (e) => {
      const clickedLabel = e.target.closest('.variant-swatch-label, .variant-button-label');
      if (clickedLabel) {
        const radio = clickedLabel.querySelector('input[type="radio"]');
        if (radio) {
          // Find all swatches/buttons in the same option group
          const optionGroup = clickedLabel.closest('.addon-option');
          if (optionGroup) {
            // Handle swatches - remove active class from all in this group
            if (clickedLabel.classList.contains('variant-swatch-label')) {
              optionGroup.querySelectorAll('.variant-swatch-label').forEach(label => {
                label.classList.remove(PRODUCT_CONFIG.addOn.activeSwatchClass);
              });
              // Add active class only to the clicked swatch
              clickedLabel.classList.add(PRODUCT_CONFIG.addOn.activeSwatchClass);
            } 
            // Handle buttons - remove active class from all in this group
            else if (clickedLabel.classList.contains('variant-button-label')) {
              optionGroup.querySelectorAll('.variant-button-label').forEach(label => {
                label.classList.remove(PRODUCT_CONFIG.addOn.activeButtonClass);
              });
              // Add active class only to the clicked button
              clickedLabel.classList.add(PRODUCT_CONFIG.addOn.activeButtonClass);
            }
          }
        }
      }
    }, true); // Use capture phase to handle this before other handlers
    
    // Rest of the existing method code...
    // Add listeners to main product group
    if (this.mainProductGroup) {
      const radios = this.mainProductGroup.querySelectorAll(PRODUCT_CONFIG.addOn.addonVariantSelector);
      
      radios.forEach(radio => {
        radio.addEventListener('change', () => {
          if (radio.checked && radio.value !== 'none') {
            const newValue = radio.value.toLowerCase();
            
            // Only update if value changed
            if (this.currentMainProduct !== newValue) {
              this.currentMainProduct = newValue;
              
              // If configurator is active, update the base image and overlays
              if (this.isConfiguratorActive) {
                this.showBaseImage();
                this.updateOverlays();
              } else {
                // NEW: Always try to show the config base image first
                const hasConfigImage = this.showConfigBaseImage(this.currentMainProduct);
                
                // If we couldn't find a config image, fall back to regular variant images
                if (!hasConfigImage) {
                  this.showVariantImages(this.currentMainProduct);
                }
              }
            }
          }
        });
      });
      
      // Initially check if we should show config images
      if (this.currentMainProduct) {
        // NEW: Try to show config image first on initialization
        const hasConfigImage = this.showConfigBaseImage(this.currentMainProduct);
        
        // If no config image found, show regular variant images
        if (!hasConfigImage) {
          this.showVariantImages(this.currentMainProduct);
        }
      }
    }
  }

  // Fixed preserveAddonConfigImages to only show in appropriate context
  preserveAddonConfigImages() {
    // If configurator isn't active, nothing to preserve
    if (!this.isConfiguratorActive) {
      return;
    }
        
    // Find all addon config images matching ANY active config types
    const mediaItems = document.querySelectorAll('.product__media-item');
    
    // First collect all active configs (we might have multiple)
    const activeConfigs = [];
    
    if (this.currentEartips) {
      activeConfigs.push({
        type: 'config_eartips',
        value: this.currentEartips
      });
    }
    
    if (this.currentChestpiece) {
      activeConfigs.push({
        type: 'config_chestpiece',
        value: this.currentChestpiece
      });
    }
    
    // Also check lastConfigType/Value for any additional configs
    if (this.lastConfigType && this.lastConfigValue) {
      // Only add if it's not already in the list
      const configExists = activeConfigs.some(
        config => config.type === this.lastConfigType && config.value === this.lastConfigValue
      );
      
      if (!configExists) {
        activeConfigs.push({
          type: this.lastConfigType,
          value: this.lastConfigValue
        });
      }
    }
        
    // Get the active media item and thumbnail
    const activeMediaItem = document.querySelector('.product__media-item.is-active');
    const activeMediaId = activeMediaItem?.dataset?.mediaId;
    const activeThumbnail = activeMediaId ? 
      document.querySelector(`.thumbnail-list__item[data-target="${activeMediaId}"]`) : 
      null;
      
    // IMPORTANT: Check if active media is the main pdp-main-image
    const isPdpMainImage = activeMediaItem?.closest('.pdp-main-image') !== null;
    
    // Process all media items to find matches for ANY active config
    mediaItems.forEach(item => {
      const img = item.querySelector('img');
      if (!img) return;
      
      const altText = img.alt || '';
      
      // Skip if not an addon config image
      if (!altText.includes('Image:')) return;
      
      // Check if this image matches ANY of our active configs
      let matchingConfig = null;
      for (const config of activeConfigs) {
        if (altText.toLowerCase().includes(config.value.toLowerCase()) && 
            altText.toLowerCase().includes(`for ${config.type.toLowerCase()}`)) {
          matchingConfig = config;
          break;
        }
      }
      
      if (matchingConfig) {
        // IMPORTANT CHANGE: For the main pdp image, we only want to show config images related to what's being viewed
        // So we check if we're viewing the main image AND this item is NOT the current active config type
        const isDisplayingMainImage = isPdpMainImage && this.lastConfigType && this.lastConfigValue;
        
        if (isDisplayingMainImage) {
          // Only show config images that match the current active type being viewed
          if (altText.toLowerCase().includes(`for ${this.lastConfigType.toLowerCase()}`) &&
              altText.toLowerCase().includes(this.lastConfigValue.toLowerCase())) {
            // This is the specific config image we're currently viewing - show it
            item.style.display = item.dataset.originalDisplay || '';
            item.style.removeProperty('display');
          } else {
            // This is a different config image (like chestpiece when viewing eartips) - hide it in main pdp
            item.style.display = 'none';
          }
        } else {
          // For other contexts (like thumbnails or other views), show all config images
          item.style.display = item.dataset.originalDisplay || '';
          item.style.removeProperty('display');
        }
        
        // Always ensure corresponding thumbnails stay visible
        const mediaId = item.dataset.mediaId;
        if (mediaId) {
          const thumb = document.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]`);
          if (thumb) {
            thumb.style.display = thumb.dataset.originalDisplay || '';
            thumb.style.removeProperty('display');
          }
        }
      }
    });
  } 

  // Modified showMatchingConfigImages method to properly hide previous images
  showMatchingConfigImages(configType, optionValue) {
    // Store the selected config for THIS specific type
    if (configType === 'config_eartips') {
      // First hide ALL eartips images before showing the new ones
      this.hideMatchingConfigImages('config_eartips');
      this.lastEartipsValue = optionValue;
    } else if (configType === 'config_chestpiece') {
      // First hide ALL chestpiece images before showing the new ones
      this.hideMatchingConfigImages('config_chestpiece');
      this.lastChestpieceValue = optionValue;
    }
    
    // Store the generic config state tracker too (for backwards compatibility)
    this.lastConfigType = configType;
    this.lastConfigValue = optionValue;
    
    // Find all media items to check for addon config images
    const mediaItems = document.querySelectorAll('.product__media-item');
    let foundImages = false;
    let firstMatchingImage = null;
    
    // Now look for and display addon config images
    mediaItems.forEach(item => {
      const img = item.querySelector('img');
      if (!img) return;
      
      const altText = img.alt || '';
      
      // Look specifically for "Image: [value] for [configType]" pattern
      const isMatchingImage = 
        altText.includes('Image:') && 
        altText.toLowerCase().includes(optionValue.toLowerCase()) && 
        altText.toLowerCase().includes(`for ${configType.toLowerCase()}`);
      
      if (isMatchingImage) {
        foundImages = true;
        
        // Show this image with important flag to ensure it's visible
        item.style.display = item.dataset.originalDisplay || 'block';
        item.style.removeProperty('display');
        item.style.setProperty('display', item.dataset.originalDisplay || 'block', 'important');
        
        // Keep track of the first matching image
        if (!firstMatchingImage) {
          firstMatchingImage = item;
        }
        
        // Also ensure corresponding thumbnail is visible
        const mediaId = item.dataset.mediaId;
        const thumbnail = document.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]`);
        if (thumbnail) {
          thumbnail.style.display = thumbnail.dataset.originalDisplay || 'block';
          thumbnail.style.removeProperty('display');
          thumbnail.style.setProperty('display', thumbnail.dataset.originalDisplay || 'block', 'important');
        }
      }
    });
    
    // Make sure at least one image is active (prioritize addon config images)
    if (!document.querySelector('.product__media-item.is-active')) {
      // If no image is active and we found matching addon images, prioritize the first matching one
      if (firstMatchingImage) {
        // First remove active class from all images
        mediaItems.forEach(item => {
          item.classList.remove('is-active');
        });
        
        // Then add active class to first matching image
        firstMatchingImage.classList.add('is-active');
        
        // NEW: Force active slide to be visible
        firstMatchingImage.style.setProperty('display', 'block', 'important');
        
        // Also make the corresponding thumbnail active
        const mediaId = firstMatchingImage.dataset.mediaId;
        const thumbnails = document.querySelectorAll('.thumbnail-list__item');
        thumbnails.forEach(thumb => {
          thumb.classList.remove('is-active');
          if (thumb.dataset.target === mediaId) {
            thumb.classList.add('is-active');
            thumb.style.display = '';
            thumb.style.removeProperty('display');
            // NEW: Force active thumbnail to be visible
            thumb.style.setProperty('display', 'block', 'important');
          }
        });
      }
    }
    
    // If still no active image, show the base image
    if (!document.querySelector('.product__media-item.is-active') && this.currentMainProduct) {
      this.showBaseImage();
    }
    
    // Update the overlay gallery if it exists
    if (window.galleryOverlay) {
      setTimeout(() => {
        window.galleryOverlay.updateFilteredSlides();
      }, 100);
    }
    
    return foundImages;
  }
  
  // Updated method to hide the matching config images
  hideMatchingConfigImages(configType) {
    // Update only the specific config type being reset
    if (configType === 'config_eartips') {
      this.lastEartipsValue = null;
    } else if (configType === 'config_chestpiece') {
      this.lastChestpieceValue = null;
    }
    
    // Also update generic tracker for backwards compatibility
    if (this.lastConfigType === configType) {
      this.lastConfigType = null;
      this.lastConfigValue = null;
    }
    
    const mediaItems = document.querySelectorAll('.product__media-item');
    let wasActiveHidden = false;
    let hiddenCount = 0;
    
    mediaItems.forEach(item => {
      const img = item.querySelector('img');
      if (!img) return;
      
      const altText = img.alt || '';
      
      // Look specifically for "Image:" and match the config type
      if (altText.includes('Image:') && altText.toLowerCase().includes(`for ${configType.toLowerCase()}`)) {
        
        // Check if this was an active image
        if (item.classList.contains('is-active')) {
          wasActiveHidden = true;
          item.classList.remove('is-active');
        }
        
        // Immediately hide with important flag
        item.style.setProperty('display', 'none', 'important');
        hiddenCount++;
        
        // Also hide thumbnails
        const mediaId = item.dataset.mediaId;
        const thumbnail = document.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]`);
        if (thumbnail) {
          thumbnail.classList.remove('is-active');
          thumbnail.style.setProperty('display', 'none', 'important');
        }
      }
    });
    
    // If we hid the active image, activate another visible one
    if (wasActiveHidden) {
      this.findAndActivateFirstVisibleImage();
    }
    
    // Update the overlay gallery if it exists
    if (window.galleryOverlay) {
      setTimeout(() => {
        window.galleryOverlay.updateFilteredSlides();
      }, 100);
    }
    
    return hiddenCount;
  }

  findAndActivateFirstVisibleImage() {
    // First, get all media items
    const mediaItems = document.querySelectorAll('.product__media-item');
    
    // Check if any image is currently active
    const hasActiveImage = document.querySelector('.product__media-item.is-active');
    
    // Only proceed if no image is currently active
    if (!hasActiveImage) {
      // First try to show the base image for the main product
      if (this.currentMainProduct) {
        const baseImageData = this.findBaseImageForVariant(this.currentMainProduct);
        if (baseImageData) {
          mediaItems.forEach(item => item.classList.remove('is-active'));
          baseImageData.mediaItem.classList.add('is-active');
          baseImageData.mediaItem.style.setProperty('display', 'block', 'important');
          
          // Update thumbnails
          const mediaId = baseImageData.mediaItem.dataset.mediaId;
          document.querySelectorAll('.thumbnail-list__item').forEach(thumb => {
            thumb.classList.remove('is-active');
            if (thumb.dataset.target === mediaId) {
              thumb.classList.add('is-active');
              thumb.style.removeProperty('display');
              thumb.style.setProperty('display', 'block', 'important');
            }
          });
          
          return;
        }
      }
      
      // If no base image, look for any visible image
      const visibleImage = Array.from(mediaItems).find(item => {
        // Skip images that are explicitly hidden
        if (item.style.display === 'none') return false;
        
        // Get computed style to check if it's really visible
        const computedStyle = window.getComputedStyle(item);
        if (computedStyle.display === 'none') return false;
        
        // Skip addon images
        const img = item.querySelector('img');
        if (img && img.alt && img.alt.includes('Image:')) return false;
        
        return true;
      });
      
      if (visibleImage) {
        // Make this image active
        mediaItems.forEach(item => item.classList.remove('is-active'));
        visibleImage.classList.add('is-active');
        visibleImage.style.setProperty('display', 'block', 'important');
        
        // Update thumbnails
        const mediaId = visibleImage.dataset.mediaId;
        document.querySelectorAll('.thumbnail-list__item').forEach(thumb => {
          thumb.classList.remove('is-active');
          if (thumb.dataset.target === mediaId) {
            thumb.classList.add('is-active');
            thumb.style.removeProperty('display');
            thumb.style.setProperty('display', 'block', 'important');
          }
        });
      }
    }
  }  

  showConfigBaseImage(variant) {
    if (!variant) return false;
        
    // Find the base image for this variant
    const baseImageData = this.findBaseImageForVariant(variant);
    
    if (!baseImageData) {
      return false;
    }
    
    // First hide any current active images that might be showing
    this.mediaItems.forEach(item => {
      // If this is already the config image we're looking for, leave it alone
      if (item === baseImageData.mediaItem) return;
      
      // Otherwise, if it's active, hide it
      if (item.classList.contains('is-active')) {
        item.classList.remove('is-active');
        item.style.display = 'none';
      }
    });
    
    // Show the base config image
    baseImageData.mediaItem.style.display = baseImageData.mediaItem.dataset.originalDisplay || '';
    baseImageData.mediaItem.classList.add('is-active');
    
    // Update thumbnails to match
    this.updateThumbnailsPreserveOriginals();
    
    // Add any active overlays if the configurator is active
    if (this.isConfiguratorActive) {
      this.updateOverlays();
    }

    this.updateStickyPreview();
    
    return true; // Success
  }  
  
  // Updated showVariantImages to ensure thumbnails are properly filtered
  showVariantImages(variant) {
    // If configurator is active, don't change the regular gallery
    if (this.isConfiguratorActive) return;
    
    const variantLower = variant.toLowerCase();
    
    // NEW: First try to show the config base image for this variant
    const baseImageData = this.findBaseImageForVariant(variant);
    
    if (baseImageData) {
      // We found a config base image for this variant, show it instead of regular images
      
      // Hide all images first
      this.mediaItems.forEach(item => {
        item.classList.remove('is-active');
        item.style.display = 'none';
      });
      
      // Show the config base image
      baseImageData.mediaItem.style.display = baseImageData.mediaItem.dataset.originalDisplay || '';
      baseImageData.mediaItem.classList.add('is-active');
      
      // Update thumbnails
      this.updateThumbnails();
      return;
    }
    
    // If no config base image, fall back to regular behavior...
    // Find regular images for this variant
    const regularImagesForVariant = this.regularImages[variantLower] || [];
    
    // First, clear all active states
    this.mediaItems.forEach(item => {
      item.classList.remove('is-active');
    });
    
    // Get the first image specifically for this variant
    let firstVariantImage = null;
    
    // Handle all media items
    this.mediaItems.forEach(item => {
      // Hide all overlay and configurator base images
      const mediaImg = item.querySelector('img');
      const altText = mediaImg?.alt || '';
      
      const isConfigImage = altText.includes('for config_');
      
      if (isConfigImage) {
        // Hide all configurator images initially
        item.style.display = 'none';
        return;
      }
      
      // Check if this image is for the current variant
      const isForThisVariant = altText.includes(`Color: ${variant}`) || 
                               altText.toLowerCase().includes(`color: ${variantLower}`);
      
      // Show generic images (those without color specification)
      const isGenericImage = !altText.includes('Color:');
      
      if (isForThisVariant || isGenericImage) {
        // Show this image since it's for the current variant or generic
        item.style.display = item.dataset.originalDisplay || '';
        
        // Keep track of the first variant-specific image (not generic)
        if (isForThisVariant && !firstVariantImage) {
          firstVariantImage = item;
        }
      } else {
        // Hide images for other variants
        item.style.display = 'none';
      }
    });
    
    // Activate the first variant-specific image
    if (firstVariantImage) {
      firstVariantImage.classList.add('is-active');
    } else {
      // If no variant-specific image was found, activate the first visible image
      const firstVisible = Array.from(this.mediaItems).find(
        item => item.style.display !== 'none'
      );
      if (firstVisible) {
        firstVisible.classList.add('is-active');
      }
    }
    
    // Update thumbnails to match the visible images
    this.updateThumbnails();
  }

  // Final version of updateThumbnailsPreserveOriginals method with proper filtering
  updateThumbnailsPreserveOriginals() {
    // Find all thumbnails
    const thumbnails = document.querySelectorAll('.thumbnail-list__item');
    
    // First, remove active class from all thumbnails and hide them
    thumbnails.forEach(thumb => {
      thumb.classList.remove('is-active');
      thumb.style.display = 'none'; // Hide all by default
    });
    
    // Get the active media item
    const activeMediaItem = document.querySelector('.product__media-item.is-active');
    if (activeMediaItem) {
      const mediaId = activeMediaItem.dataset.mediaId;
      
      // Find and activate the corresponding thumbnail
      const activeThumbnail = document.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]`);
      if (activeThumbnail) {
        activeThumbnail.classList.add('is-active');
        activeThumbnail.style.display = '';
      }
    }
    
    // Only show thumbnails for the current variant and the active config base image
    if (this.currentMainProduct) {
      const variantLower = this.currentMainProduct.toLowerCase();
      
      // Now show thumbnails for regular images of current variant
      thumbnails.forEach(thumb => {
        const mediaId = thumb.dataset.target;
        const mediaItem = document.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
        
        if (mediaItem) {
          const mediaImg = mediaItem.querySelector('img');
          const altText = mediaImg?.alt || '';
          
          // Check if this thumbnail is for the current variant (regular images)
          const isForThisVariant = 
            altText.includes(`Color: ${this.currentMainProduct}`) || 
            altText.toLowerCase().includes(`color: ${variantLower}`);
          
          // Show generic images (those without color specification)
          const isGenericImage = !altText.includes('Color:');
          
          // Check if this is the selected config base image
          const isConfigBaseForCurrentVariant = 
            altText.includes('for config_main') && 
            (altText.includes(`Color: ${this.currentMainProduct}`) || 
             altText.toLowerCase().includes(`color: ${variantLower}`));
          
          // Hide all other config images that are NOT for the current variant
          const isOtherConfigImage = 
            altText.includes('for config_') && 
            !isConfigBaseForCurrentVariant;
          
          // For filtering, only show regular images and the config base image for this variant 
          if ((isForThisVariant || isGenericImage) && !altText.includes('for config_')) {
            thumb.style.display = thumb.dataset.originalDisplay || '';
          } else if (isConfigBaseForCurrentVariant) {
            // Show the specific config base image for the current variant
            thumb.style.display = thumb.dataset.originalDisplay || '';
          }
        }
      });
      
      // Make sure active thumbnail is always visible
      const activeThumbnail = document.querySelector('.thumbnail-list__item.is-active');
      if (activeThumbnail) {
        activeThumbnail.style.display = '';
      }
    }
  }
  
  // Modified updateThumbnails method to preserve originals
  updateThumbnails() {
    // Get all thumbnails
    const thumbnails = document.querySelectorAll('.thumbnail-list__item');
    
    // First, remove active class from all thumbnails and hide them
    thumbnails.forEach(thumb => {
      thumb.classList.remove('is-active');
      thumb.style.display = 'none'; // Hide all by default
    });
    
    // Now match thumbnails with the visible/hidden state of media items
    thumbnails.forEach(thumb => {
      const mediaId = thumb.dataset.target;
      const mediaItem = document.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
      
      if (mediaItem) {
        // Match thumbnail visibility with media item - only show if visible
        thumb.style.display = (mediaItem.style.display === 'none') ? 'none' : '';
        
        // Set active class if the media item is active
        if (mediaItem.classList.contains('is-active')) {
          thumb.classList.add('is-active');
        }
      }
    });
  }
  
  preloadImages() {
    const overlayTypes = ['eartips', 'chestpiece'];
    
    // Preload all overlay images
    overlayTypes.forEach(type => {
      Object.keys(this.overlayImages[type]).forEach(color => {
        const imgData = this.overlayImages[type][color];
        const img = new Image();
        img.src = imgData.src;
        // Store the loaded image
        this.overlayImages[type][color].image = img;
      });
    });
  }
  
  // Updated activateConfigurator to ensure proper thumbnail display
  activateConfigurator() {
    this.isConfiguratorActive = true;
    this.viewingRegularImageInConfigMode = false;
    
    // Store current visible thumbnails for variant
    this.originalVisibleThumbnails = Array.from(document.querySelectorAll('.thumbnail-list__item'))
      .filter(thumb => thumb.style.display !== 'none')
      .map(thumb => thumb.dataset.target);
        
    // First, hide all active images to prevent any overlap
    this.mediaItems.forEach(item => {
      if (item.classList.contains('is-active')) {
        item.classList.remove('is-active');
      }
    });
    
    // Remove any existing overlays before showing new ones
    this.removeAllOverlays();
    
    // Show base image for current main product
    this.showBaseImage();
    
    // Show any additional images that match our config types
    if (this.lastConfigType && this.lastConfigValue) {
      this.showMatchingConfigImages(this.lastConfigType, this.lastConfigValue);
    }
    
    // Apply overlays to both main image and thumbnail
    this.updateOverlays();
    
    // Add thumbnail click handler for configurator mode
    this.handleThumbnailClicksInConfiguratorMode();
  
    this.updateStickyPreview();
  } 

  // Updated handleThumbnailClicksInConfiguratorMode
  handleThumbnailClicksInConfiguratorMode() {
    // Remove any existing event listeners first
    if (this.thumbnailClickHandler) {
      this.thumbnailList.removeEventListener('click', this.thumbnailClickHandler);
    }
    
    // Create and store the event handler
    this.thumbnailClickHandler = (e) => {
      // Find the thumbnail item that was clicked
      const thumbnailItem = e.target.closest('.thumbnail-list__item');
      if (!thumbnailItem) return;
      
      // Get the media ID this thumbnail corresponds to
      const mediaId = thumbnailItem.dataset.target;
      if (!mediaId) return;
      
      // Find the corresponding media item
      const mediaItem = document.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
      if (!mediaItem) return;
      
      // Check if this is a config image or overlay
      const mediaImg = mediaItem.querySelector('img');
      const altText = mediaImg?.alt || '';
      const isConfigImage = altText.includes('for config_');
      
      // Special tracking for active config images
      // Store information about ALL current configs before making changes
      const allActiveConfigs = [];
      
      // Track eartips config if active
      if (this.currentEartips) {
        allActiveConfigs.push({
          type: 'config_eartips',
          value: this.currentEartips
        });
      }
      
      // Track chestpiece config if active 
      if (this.currentChestpiece) {
        allActiveConfigs.push({
          type: 'config_chestpiece',
          value: this.currentChestpiece
        });
      }
            
      // Find which config is this thumbnail (if any)
      let clickedConfigType = null;
      if (altText.includes('Image:')) {
        if (altText.includes('for config_eartips')) {
          clickedConfigType = 'config_eartips';
        } else if (altText.includes('for config_chestpiece')) {
          clickedConfigType = 'config_chestpiece';
        }
      }
      
      if (!isConfigImage) {
        // It's a regular image - show it and hide all config images
        
        // First mark all images as not active
        this.mediaItems.forEach(item => {
          item.classList.remove('is-active');
          item.style.display = 'none';
        });
        
        // Make the clicked item active
        mediaItem.classList.add('is-active');
        mediaItem.style.display = mediaItem.dataset.originalDisplay || '';
        
        // Update thumbnail active states
        document.querySelectorAll('.thumbnail-list__item').forEach(thumb => {
          thumb.classList.remove('is-active');
        });
        thumbnailItem.classList.add('is-active');
        
        // Remove overlays when a regular image is clicked
        this.removeAllOverlays();
        
        // Set flag that we're viewing a regular image in config mode
        this.viewingRegularImageInConfigMode = true;
        
        // Here we differ from previous implementation:
        // DON'T unconditionally force display the config images in the main view
        // Instead, let preserveAddonConfigImages handle it context-appropriately
        setTimeout(() => {
          this.preserveAddonConfigImages();
        }, 10);
        
      } else if (altText.includes('for config_main')) {
        // If it's a main config base image, show it with overlays
        this.viewingRegularImageInConfigMode = false;
        
        // Make the clicked config image active
        this.mediaItems.forEach(item => {
          item.classList.remove('is-active');
          item.style.display = 'none';
        });
        
        // Show the clicked media item
        mediaItem.classList.add('is-active');
        mediaItem.style.display = mediaItem.dataset.originalDisplay || '';
        
        // Update thumbnail active states
        document.querySelectorAll('.thumbnail-list__item').forEach(thumb => {
          thumb.classList.remove('is-active');
        });
        thumbnailItem.classList.add('is-active');
        
        // Apply overlays
        this.updateOverlays();
        
      } else if (clickedConfigType) {
        // If it's a addon config image thumbnail being clicked, show just that config type
        this.viewingRegularImageInConfigMode = false;
        
        // Extract the value from the alt text
        let clickedConfigValue = '';
        if (altText.includes('Image:')) {
          const match = altText.match(/Image:\s*(.*?)\s+for/i);
          if (match && match[1]) {
            clickedConfigValue = match[1].trim();
          }
        }
        
        // Update last config type/value to just this config
        this.lastConfigType = clickedConfigType;
        this.lastConfigValue = clickedConfigValue;
        
        // Make the clicked config image active
        this.mediaItems.forEach(item => {
          item.classList.remove('is-active');
          
          // Hide all except the clicked image
          if (item !== mediaItem) {
            item.style.display = 'none';
          }
        });
        
        // Show the clicked media item
        mediaItem.classList.add('is-active');
        mediaItem.style.display = mediaItem.dataset.originalDisplay || '';
        
        // Update thumbnail active states
        document.querySelectorAll('.thumbnail-list__item').forEach(thumb => {
          thumb.classList.remove('is-active');
        });
        thumbnailItem.classList.add('is-active');
        
        // Only show this specific config image type
        setTimeout(() => {
          this.preserveAddonConfigImages();
        }, 10);
      }
      
      // Always ensure proper thumbnail visibility
      this.updateThumbnailsPreserveOriginals();
    };
    
    // Attach the event listener
    this.thumbnailList.addEventListener('click', this.thumbnailClickHandler);
  }
  
  // Improved deactivateConfigurator method to clean up event handlers
  deactivateConfigurator() {
    this.isConfiguratorActive = false;
    
    // Remove all overlays from both main images and thumbnails
    this.removeAllOverlays();
    
    // Reset overlay active state
    this.isOverlayActive = false;
    this.viewingRegularImageInConfigMode = false;
    
    // Remove the thumbnail click handler
    if (this.thumbnailClickHandler) {
      this.thumbnailList.removeEventListener('click', this.thumbnailClickHandler);
      this.thumbnailClickHandler = null;
    }
    
    // Show regular variant images
    if (this.currentMainProduct) {
      this.showVariantImages(this.currentMainProduct);
    }

    this.updateStickyPreview();

  }
  
  // Updated showBaseImage method
  showBaseImage() {
    // Find the base image for the current main product
    if (!this.currentMainProduct) {
      return;
    }
    
    // First, make sure all images are hidden and inactive
    this.mediaItems.forEach(item => {
      item.classList.remove('is-active');
      item.style.display = 'none';
    });
    
    // Get the base image media item
    const baseImageData = this.findBaseImageForVariant(this.currentMainProduct);
    
    if (!baseImageData) {
      
      // Try to find a regular image for this variant instead
      const regularImages = this.regularImages[this.currentMainProduct.toLowerCase()] || [];
      if (regularImages.length > 0) {
        const mainImage = regularImages[0];
        mainImage.mediaItem.style.display = mainImage.mediaItem.dataset.originalDisplay || '';
        mainImage.mediaItem.classList.add('is-active');
      }
      
      // Update thumbnails properly
      this.updateThumbnailsPreserveOriginals();
      return;
    }
    
    // Show only the base image for the configurator
    baseImageData.mediaItem.style.display = baseImageData.mediaItem.dataset.originalDisplay || '';
    baseImageData.mediaItem.classList.add('is-active');
    
    // Update thumbnails with our improved method
    this.updateThumbnailsPreserveOriginals();
    this.updateStickyPreview();
  }
  
  findBaseImageForVariant(variant) {
    // Try exact match first
    const variantLower = variant.toLowerCase();
    let baseImage = this.baseImages[variantLower];
    
    if (baseImage) {
      return baseImage;
    }
    
    // Try case-insensitive match
    const baseImageKey = Object.keys(this.baseImages).find(key => 
      key.toLowerCase() === variantLower
    );
    
    if (baseImageKey) {
      return this.baseImages[baseImageKey];
    }
    
    return null;
  }
  
  // Updated updateOverlays method to handle blank image issues
  updateOverlays() {
    // Remove existing overlays
    this.removeAllOverlays();
    
    // If we're viewing a regular image in config mode, don't add overlays
    if (this.viewingRegularImageInConfigMode) {      
      // Even though we're not adding overlays, ensure addon config images remain properly visible
      this.preserveAddonConfigImages();
      return;
    }
    
    // Check if we have options to show
    if (!this.currentEartips && !this.currentChestpiece) {
      // Still preserve addon config images
      this.preserveAddonConfigImages();
      return;
    }
    
    // Find the current active base image and its matching thumbnail
    const activeMediaItem = document.querySelector('.product__media-item.is-active');
    if (!activeMediaItem) {
      // Still preserve addon config images
      this.preserveAddonConfigImages();
      return;
    }
    
    // Get the media ID to find the corresponding thumbnail
    const mediaId = activeMediaItem.dataset.mediaId;
    const activeThumbnail = document.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]`);
    
    // Ensure the active media item is visible
    activeMediaItem.style.display = activeMediaItem.dataset.originalDisplay || '';
    
    // Find a suitable container for overlays in the main image
    let overlayContainer = null;
    
    // First try media wrapper
    overlayContainer = activeMediaItem.querySelector('.product__media-wrapper');
    
    // If not found, try direct media element
    if (!overlayContainer) {
      overlayContainer = activeMediaItem.querySelector('.product__media');
    }
    
    // If still not found, use the media item itself
    if (!overlayContainer) {
      overlayContainer = activeMediaItem;
    }
    
    if (!overlayContainer) {
      console.error('Could not find suitable container for overlays');
      // Still preserve addon config images
      this.preserveAddonConfigImages();
      return;
    }
    
    // Create and add overlays to main image
    this.createOverlay(this.currentEartips, 'eartips', activeMediaItem, overlayContainer);
    this.createOverlay(this.currentChestpiece, 'chestpiece', activeMediaItem, overlayContainer);
    
    // Also add overlays to the thumbnail if found
    if (activeThumbnail) {
      // Find a container in the thumbnail
      let thumbContainer = activeThumbnail;
      
      // Create and add overlays to thumbnail
      this.createThumbnailOverlay(this.currentEartips, 'eartips', activeThumbnail, thumbContainer);
      this.createThumbnailOverlay(this.currentChestpiece, 'chestpiece', activeThumbnail, thumbContainer);
    }
    
    // Ensure addon config images remain visible in the correct context
    this.preserveAddonConfigImages();
  
    // Notify gallery overlay about configuration change
    const event = new CustomEvent('configurator:update', {
      detail: {
        mainProduct: this.currentMainProduct,
        eartips: this.currentEartips,
        chestpiece: this.currentChestpiece
      }
    });
    document.dispatchEvent(event);

    // Ensure the active slide is always visible
    const activeSlide = document.querySelector('.product__media-item.is-active');
    if (activeSlide) {
      activeSlide.style.setProperty('display', 'block', 'important');
    }
    
    this.isOverlayActive = true;
    this.updateStickyPreview();
  }

  
  
  createOverlay(colorName, overlayType, mediaItem, container) {
    if (!colorName || !container) return;
    
    // Find the overlay image data
    const overlayData = this.findOverlayImage(colorName, overlayType);
    if (!overlayData) {
      return;
    }
    
    // Create overlay element
    const overlay = document.createElement('div');
    overlay.className = `product-config-overlay ${overlayType}-overlay`;
    overlay.dataset.overlayType = overlayType;
    overlay.dataset.color = colorName;
    
    // Position the overlay absolutely to match base image
    overlay.style.cssText = `
      display: block;
      max-width: 100%;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 5;
      overflow: hidden;
    `;
    
    // Create image element for overlay
    const img = document.createElement('img');
    img.className = 'product-configurator-overlay-img';
    img.src = overlayData.src;
    img.alt = `${overlayType} overlay - ${colorName}`;
    img.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: cover;
    `;
    
    overlay.appendChild(img);
    container.appendChild(overlay);
    
    // Add to our active overlays list for tracking
    this.activeOverlays.push({
      element: overlay,
      type: overlayType,
      color: colorName
    }); 
  }
  
  createThumbnailOverlay(colorName, overlayType, thumbnailItem, container) {
    if (!colorName || !container) return;
    
    // Find the overlay image data
    const overlayData = this.findOverlayImage(colorName, overlayType);
    if (!overlayData) {
      return;
    }
    
    // Create overlay element for thumbnail
    const thumbOverlay = document.createElement('div');
    thumbOverlay.className = `thumbnail-config-overlay ${overlayType}-thumbnail-overlay`;
    thumbOverlay.dataset.overlayType = overlayType;
    thumbOverlay.dataset.color = colorName;
    
    // Position the overlay absolutely to match the thumbnail
    thumbOverlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 5;
    `;
    
    // Create image element for overlay
    const thumbImg = document.createElement('img');
    thumbImg.className = 'thumbnail-configurator-overlay-img';
    thumbImg.src = overlayData.src;
    thumbImg.alt = `${overlayType} thumbnail overlay - ${colorName}`;
    thumbImg.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: contain;
    `;
    
    thumbOverlay.appendChild(thumbImg);
    container.appendChild(thumbOverlay);
    
    // Add to our active thumbnail overlays list for tracking
    this.activeThumbnailOverlays.push({
      element: thumbOverlay,
      type: overlayType,
      color: colorName
    });
    
  }
  
  findOverlayImage(colorName, overlayType) {
    if (!this.overlayImages[overlayType]) return null;
    
    // Try exact match first
    let overlay = this.overlayImages[overlayType][colorName];
    if (overlay) return overlay;
    
    // Try case-insensitive match
    const colorLower = colorName.toLowerCase();
    const overlayKey = Object.keys(this.overlayImages[overlayType]).find(key => 
      key.toLowerCase() === colorLower
    );
    
    if (overlayKey) {
      return this.overlayImages[overlayType][overlayKey];
    }
    
    return null;
  }
  
  // Fixed version of removeAllOverlays method
  removeAllOverlays() {
    // Remove all existing overlays from main images
    this.activeOverlays.forEach(overlay => {
      if (overlay.element && overlay.element.parentNode) {
        overlay.element.parentNode.removeChild(overlay.element);
      }
    });
    
    // Remove all existing overlays from thumbnails
    this.activeThumbnailOverlays.forEach(overlay => {
      if (overlay.element && overlay.element.parentNode) {
        overlay.element.parentNode.removeChild(overlay.element);
      }
    });
    
    // Clear the arrays
    this.activeOverlays = [];
    this.activeThumbnailOverlays = [];
    
    // Use the enhanced preserveAddonConfigImages method to ensure visibility
    // of ALL addon config images regardless of which type they are
    this.preserveAddonConfigImages();
  }
  
}

// Helper function to scan for addon settings in the DOM
function scanForAddonSettings() {
  const addonBlocks = document.querySelectorAll('[data-block-type="add_on_product"]');
  
  addonBlocks.forEach(block => {
    const productId = block.dataset.productId;
    const hideImage = block.dataset.hideImage === 'true' || 
                      block.querySelector('[data-hide-image="true"]') !== null;
    
    if (productId && hideImage) {
      // Find corresponding addon-product-options
      const addonGroup = document.querySelector(`.addon-product-options[data-product-id="${productId}"]`);
      if (addonGroup) {
        // Ensure data attribute is set
        addonGroup.dataset.hideImage = 'true';
                
        // Also ensure handle is set if available
        if (block.dataset.handle && !addonGroup.dataset.handle) {
          addonGroup.dataset.handle = block.dataset.handle;
        }
        
        // Check for non-none selected options and activate if needed
        const handle = addonGroup.dataset.handle;
        if (handle) {
          const selectedRadio = addonGroup.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
          if (selectedRadio && selectedRadio.value !== 'none' && !selectedRadio.dataset.isNone) {
            setTimeout(() => {
              window.emergencyFixProductImages(handle);
            }, 500);
          }
        }
      }
    }
  });
}

// Add this new function to fix flickering issues
function preventConfigFlickering() {
  // This function will patch the ProductConfigurator instance
  if (!window.productConfigurator) {
    console.warn('Product configurator not found, cannot apply fix');
    return;
  }
  
  // Create a wrapper function around showMatchingConfigImages
  const originalShowMatchingConfigImages = window.productConfigurator.showMatchingConfigImages;
  
  window.productConfigurator.showMatchingConfigImages = function(configType, optionValue) {
    // First, hide ALL images to prevent flickering
    document.querySelectorAll('.product__media-item').forEach(item => {
      // Save current display state if not already saved
      if (!item.dataset.originalDisplay) {
        item.dataset.originalDisplay = item.style.display || '';
      }
      
      // Hide immediately with !important
      item.style.setProperty('display', 'none', 'important');
    });
    
    // Use setTimeout with 0ms to push to next event loop, ensuring DOM updates
    setTimeout(() => {
      // Call original function after hiding everything
      originalShowMatchingConfigImages.call(window.productConfigurator, configType, optionValue);
    }, 0);
    
    // Return true - the real work will happen in the setTimeout
    return true;
  };
  
  // Same for other critical methods that affect image visibility
  const originalUpdateOverlays = window.productConfigurator.updateOverlays;
  
  window.productConfigurator.updateOverlays = function() {
    // First hide all images to prevent flickering
    document.querySelectorAll('.product__media-item:not(.is-active)').forEach(item => {
      item.style.setProperty('display', 'none', 'important');
    });
    
    // Call original function after short delay
    setTimeout(() => {
      originalUpdateOverlays.call(window.productConfigurator);
    }, 0);
  };
  
}

// Emergency fix for product images - call this directly from your browser console
window.emergencyFixProductImages = function(handle) {
  const targetHandle = handle || "core-500-stethoscope-case";
  
  // Add a style tag for the emergency fix if it doesn't exist
  if (!document.getElementById('emergency-image-fix-styles')) {
    const styleTag = document.createElement('style');
    styleTag.id = 'emergency-image-fix-styles';
    styleTag.innerHTML = `
      .emergency-visible {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        position: relative !important;
      }
      
      .product-gallery-fixed .is-active {
        display: block !important;
        visibility: visible !important;
      }
    `;
    document.head.appendChild(styleTag);
  }
  
  // Mark the gallery as fixed
  const gallery = document.querySelector('.product__media-wrapper');
  if (gallery) {
    gallery.classList.add('product-gallery-fixed');
  }
  
  // Get all media items
  const allMedia = document.querySelectorAll('.product__media-item');
  const allThumbnails = document.querySelectorAll('.thumbnail-list__item');
  const allOverlaySlides = document.querySelectorAll('.overlay-gallery__slide');
  
  // Find media items with the target handle in alt text
  let foundMedia = [];
  allMedia.forEach(item => {
    const img = item.querySelector('img');
    if (!img) return;
    
    const altText = img.alt || '';
    if (altText.toLowerCase().includes(targetHandle.toLowerCase())) {
      foundMedia.push(item);
      
      // Force visibility
      item.classList.add('emergency-visible');
      // Also try inline styles
      item.style.setProperty('display', 'block', 'important');
      item.style.setProperty('visibility', 'visible', 'important');
      item.style.setProperty('opacity', '1', 'important');
      
      // Also update matching overlay slide
      const imgSrc = img.src;
      allOverlaySlides.forEach(slide => {
        const slideImg = slide.querySelector('img');
        if (slideImg && slideImg.src === imgSrc) {
          slide.dataset.visible = 'true';
        }
      });
    }
  });
  
  // Make the first found media item active
  if (foundMedia.length > 0) {
    // Remove active class from all media items
    allMedia.forEach(item => item.classList.remove('is-active'));
    
    // Make the first found media active
    foundMedia[0].classList.add('is-active');
    
    // Also force visibility on the active media
    foundMedia[0].style.setProperty('display', 'block', 'important');
    
    // Find and show the corresponding thumbnail
    const activeMediaId = foundMedia[0].dataset.mediaId;
    let foundThumbnail = false;
    
    allThumbnails.forEach(thumb => {
      if (thumb.dataset.target === activeMediaId) {
        // Make this thumbnail active and visible
        allThumbnails.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        thumb.classList.add('emergency-visible');
        thumb.style.setProperty('display', 'block', 'important');
        foundThumbnail = true;
      }
    });
    
    // If we didn't find a matching thumbnail by mediaId, try to find it by handle in alt text
    if (!foundThumbnail) {
      allThumbnails.forEach(thumb => {
        const mediaId = thumb.dataset.target;
        const mediaItem = document.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
        if (mediaItem) {
          const img = mediaItem.querySelector('img');
          if (img && img.alt && img.alt.toLowerCase().includes(targetHandle.toLowerCase())) {
            // Make this thumbnail active and visible
            allThumbnails.forEach(t => t.classList.remove('is-active'));
            thumb.classList.add('is-active');
            thumb.classList.add('emergency-visible');
            thumb.style.setProperty('display', 'block', 'important');
          }
        }
      });
    }
    
    // Update the overlay gallery if it exists
    if (window.galleryOverlay) {
      window.galleryOverlay.updateFilteredSlides();
    }
    
    return `Found and displayed ${foundMedia.length} images with handle: ${targetHandle}`;
  } else {
    
    // If no images found, log all alt texts to help troubleshoot
    allMedia.forEach((item, i) => {
      const img = item.querySelector('img');
    });
    
    return "No matching images found - see console for details";
  }
};

// Add this to handle image visibility on variant selection
function improveVariantImageVisibility() {
  // For each add-on group with hide_image=true
  const addonGroups = document.querySelectorAll('.addon-product-options[data-hide-image="true"]');
  
  addonGroups.forEach(group => {
    const handle = group.dataset.handle;
    if (!handle) return;
    
    // Get the radios
    const radios = group.querySelectorAll('input[type="radio"]');
    
    // Add improved change listeners
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          const isNone = radio.dataset.isNone === 'true' || radio.value.toLowerCase() === 'none';
                    
          if (!isNone) {
            // Use setTimeout to allow other code to run first
            setTimeout(() => {
              
              // Directly manipulate the DOM
              const mediaItems = document.querySelectorAll('.product__media-item');
              let foundAny = false;
              
              mediaItems.forEach(item => {
                const img = item.querySelector('img');
                if (!img) return;
                
                const altText = img.alt || '';
                if (altText.toLowerCase().includes(handle.toLowerCase())) {
                  
                  // Force display
                  item.style.setProperty('display', 'block', 'important');
                  item.style.setProperty('visibility', 'visible', 'important');
                  item.style.setProperty('opacity', '1', 'important');
                  
                  // Also make this the active image if we haven't found one yet
                  if (!foundAny) {
                    mediaItems.forEach(m => m.classList.remove('is-active'));
                    item.classList.add('is-active');
                    foundAny = true;
                    
                    // Also make corresponding thumbnail active
                    const mediaId = item.dataset.mediaId;
                    const thumb = document.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]`);
                    if (thumb) {
                      document.querySelectorAll('.thumbnail-list__item').forEach(t => t.classList.remove('is-active'));
                      thumb.classList.add('is-active');
                      thumb.style.setProperty('display', 'block', 'important');
                    }
                  }
                }
              });
            }, 100);
          }
        }
      });
    });
    
    // Check initial state
    const checkedRadio = group.querySelector('input[type="radio"]:checked');
    if (checkedRadio && checkedRadio.value !== 'none' && !checkedRadio.dataset.isNone) {
      // If already selected, manually trigger the change handler
      setTimeout(() => {
        const event = new Event('change');
        checkedRadio.dispatchEvent(event);
      }, 300);
    }
  });
}

// Enhanced scanForAddonSettings function with immediate action
function enhancedScanForAddonSettings() {
  const addonBlocks = document.querySelectorAll('[data-block-type="add_on_product"]');
  
  addonBlocks.forEach(block => {
    const productId = block.dataset.productId;
    const hideImage = block.dataset.hideImage === 'true';
    
    if (productId && hideImage) {
      // Find corresponding addon-product-options
      const addonGroup = document.querySelector(`.addon-product-options[data-product-id="${productId}"]`);
      if (addonGroup) {
        // Ensure data attribute is set
        addonGroup.dataset.hideImage = 'true';
                
        // Get the handle
        const handle = addonGroup.dataset.handle;
        if (handle) {
          // Check if a non-none option is selected
          const checkedRadio = addonGroup.querySelector('input[type="radio"]:checked');
          if (checkedRadio && checkedRadio.value !== 'none' && !checkedRadio.dataset.isNone) {            
            // Use setTimeout to ensure DOM is fully loaded
            setTimeout(() => {
              window.emergencyFixProductImages(handle);
            }, 500);
          }
        }
      }
    }
  });
}

// Fixed ShippingETAManager class to preserve rich text formatting
class ShippingETAManager {
  constructor() {
    // Find the engraving radio buttons
    this.engravingRadios = document.querySelectorAll('input[name="engraving-enabled"]');
    
    // Look for the shipping message container
    this.shippingMessageContainer = document.querySelector('.pdp-bundle-shipping-eta');
    
    // Get the two different shipping messages from section settings
    this.regularShippingMessage = this.shippingMessageContainer?.dataset.regularEta || '';
    this.engravingShippingMessage = this.shippingMessageContainer?.dataset.engravingEta || '';
    
    this.init();
  }
  
  init() {
    if (!this.shippingMessageContainer || !this.engravingRadios.length) {
      return;
    }
        
    // Add event listeners to radio buttons
    this.engravingRadios.forEach(radio => {
      radio.addEventListener('change', () => this.updateShippingMessage());
    });
    
    // Set initial message
    this.updateShippingMessage();
  }
  
  updateShippingMessage() {
    // Check if engraving is enabled
    const engravingEnabled = Array.from(this.engravingRadios).find(radio => 
      radio.checked && radio.value === 'enabled'
    );
    
    // Update the message based on engraving status using innerHTML to preserve formatting
    if (engravingEnabled) {
      this.shippingMessageContainer.innerHTML = this.engravingShippingMessage;
    } else {
      this.shippingMessageContainer.innerHTML = this.regularShippingMessage;
    }
  }
}

// Initialize immediately when the script runs
const shippingManager = new ShippingETAManager();

// Product Progress Bar Class
class ProductProgressBar {
  constructor() {
    // Configuration options
    this.config = {
      barClass: 'product-progress-bar',
      indicatorClass: 'progress-indicator',
      activeClass: 'active',
      offsetThreshold: 0.5, // Percentage of element height (0.5 = midpoint)
      addonSelector: '.addon-product-options',
      // Visual configuration
      barWidth: '4px',
      barHeight: '300px',
      indicatorHeight: '46px',
      activeColor: '#000000',
      inactiveColor: '#d1d1d1',
      rightOffset: '16px'
    };
    
    // State
    this.progressBar = null;
    this.indicators = [];
    this.addonSections = [];
    this.visibleSections = []; // Only sections that should be shown in progress bar
    this.addonSteps = [];
    this.isVisible = false;
    this.lastScrollTop = 0;
    this.scrollDirection = 'down';
    
    // Debounce timer for scroll handler
    this.scrollDebounceTimer = null;
    
    this.init();
  }
  
  init() {
    // Find all addon sections first
    const allAddonSections = Array.from(document.querySelectorAll(this.config.addonSelector));
    
    // Filter out sections with data-show-progress="false"
    this.visibleSections = allAddonSections.filter(section => {
      const showProgress = section.dataset.showProgress;
      // Include if data-show-progress is not set, or if it's not explicitly "false"
      return showProgress === undefined || showProgress !== "false";
    });
    
    // Store all addon sections for scroll detection
    this.addonSections = allAddonSections;
    
    // Only proceed if we have visible sections
    if (!this.visibleSections.length) {
      return;
    }
        
    // Get step titles for tooltips (only for visible sections)
    this.visibleSections.forEach(section => {
      const stepElem = section.querySelector('.addon-step');
      const title = section.querySelector('.addon-heading');
      this.addonSteps.push({
        step: stepElem ? stepElem.textContent.trim() : '',
        title: title ? title.textContent.trim() : ''
      });
    });
    
    // Add CSS styles for progress bar
    this.addStyles();
    
    // Create the progress bar
    this.createProgressBar();
    
    // Setup scroll event listener
    window.addEventListener('scroll', this.handleScroll.bind(this));
    
    // Initial check
    setTimeout(() => {
      this.checkVisibility();
      this.updateProgressIndicators();
    }, 500);
  }
  
  addStyles() {
    // Create a style element if it doesn't exist
    if (!document.getElementById('progress-bar-styles')) {
      const styleEl = document.createElement('style');
      styleEl.id = 'progress-bar-styles';
      
      // Define the styles
      styleEl.textContent = `
        .product-progress-bar {
          position: fixed;
          right: ${this.config.rightOffset};
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          flex-direction: column;
          gap: 8px;
          z-index: 100;
          transition: opacity 0.3s ease;
          padding: 10px 3px;
          opacity: 0;
        }
        
        .progress-indicator {
          width: ${this.config.barWidth};
          height: ${this.config.indicatorHeight};
          background-color: ${this.config.inactiveColor};
          transition: background-color 0.3s ease, transform 0.2s ease;
          cursor: pointer;
          position: relative;
        }
        
        .progress-indicator:hover {
          transform: scaleX(1.5);
        }
        
        .progress-indicator.active {
          background-color: ${this.config.activeColor};
          transform: scaleX(1.3);
        }
        
        .progress-indicator-tooltip,
        .progress-indicator:hover .progress-indicator-tooltip {
          display: none;;
        }
      `;
      
      // Add to document head
      document.head.appendChild(styleEl);
    }
  }
  
  createProgressBar() {
    // Create main container
    this.progressBar = document.createElement('div');
    this.progressBar.className = this.config.barClass;
    
    // Create indicators for each visible addon section
    this.visibleSections.forEach((section, index) => {
      const indicator = document.createElement('div');
      indicator.className = this.config.indicatorClass;
      
      // Add data attributes for tracking
      indicator.dataset.index = index;
      indicator.dataset.sectionId = section.id || `addon-section-${index}`;
      
      // Store reference to the actual section (for scrolling)
      indicator.dataset.sectionPosition = this.addonSections.indexOf(section);
      
      // Add tooltip if we have step info
      if (this.addonSteps[index]) {
        const tooltip = document.createElement('div');
        tooltip.className = 'progress-indicator-tooltip';
        
        // Use step number and title if available
        const stepInfo = this.addonSteps[index].step;
        const titleInfo = this.addonSteps[index].title;
        tooltip.textContent = stepInfo ? stepInfo : (titleInfo ? titleInfo : `Step ${index + 1}`);
        
        indicator.appendChild(tooltip);
      }
      
      // Add click event to scroll to this section
      indicator.addEventListener('click', () => {
        const sectionPosition = parseInt(indicator.dataset.sectionPosition, 10);
        this.scrollToSection(sectionPosition);
      });
      
      // Add to progress bar
      this.progressBar.appendChild(indicator);
      this.indicators.push(indicator);
    });
    
    // Add to document
    document.body.appendChild(this.progressBar);
  }
  
  handleScroll() {
    // Get current scroll position
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Determine scroll direction
    this.scrollDirection = scrollTop > this.lastScrollTop ? 'down' : 'up';
    this.lastScrollTop = scrollTop;
    
    // Debounce the scroll handler for better performance
    if (this.scrollDebounceTimer) {
      clearTimeout(this.scrollDebounceTimer);
    }
    
    this.scrollDebounceTimer = setTimeout(() => {
      // First check general visibility based on scroll position
      this.checkVisibility();
      
      // Then check indicator states and hide if needed
      this.updateProgressIndicators();
    }, 10);
  }
  
  // Corrected checkVisibility method for ProductProgressBar class
  checkVisibility() {
    // Only show progress bar when the first section's midpoint reaches viewport midpoint
    // and hide when last section's midpoint passes viewport midpoint
    const firstSection = this.addonSections[0];
    const lastSection = this.addonSections[this.addonSections.length - 1];
    
    if (!firstSection || !lastSection) return;
    
    const windowHeight = window.innerHeight;
    const viewportMidpoint = windowHeight / 2;
    
    // Calculate the positions of the first addon section
    const firstSectionRect = firstSection.getBoundingClientRect();
    const firstSectionMidpoint = firstSectionRect.top + (firstSectionRect.height / 2);
    
    // Add additional check for the first section (for scrolling up)
    // If we're scrolling up and the first section's top is below the viewport midpoint,
    // we should hide the progress bar
    if (this.scrollDirection === 'up' && firstSectionMidpoint > viewportMidpoint) {
      if (this.isVisible) {
        this.isVisible = false;
        this.progressBar.style.opacity = '0';
        this.progressBar.style.pointerEvents = 'none';
      }
      return; // Exit early
    }
    
    // Continue with the regular visibility check for scrolling down
    const lastSectionRect = lastSection.getBoundingClientRect();
    const lastSectionMidpoint = lastSectionRect.top + (lastSectionRect.height / 2);
    
    // Show the progress bar when:
    // 1. The first section's midpoint is at or above the viewport midpoint
    // 2. The last section's midpoint is still below the viewport midpoint 
    const firstMidpointAtOrAboveViewportMid = firstSectionMidpoint <= viewportMidpoint;
    const lastMidpointBelowViewportMid = lastSectionMidpoint >= viewportMidpoint;
    
    const shouldBeVisible = firstMidpointAtOrAboveViewportMid && lastMidpointBelowViewportMid;
    
    // Update visibility
    if (shouldBeVisible && !this.isVisible) {
      this.isVisible = true;
      this.progressBar.style.opacity = '1';
      this.progressBar.style.pointerEvents = 'auto';
    } else if (!shouldBeVisible && this.isVisible) {
      this.isVisible = false;
      this.progressBar.style.opacity = '0';
      this.progressBar.style.pointerEvents = 'none';
    }
  }
  
  // Keep our improved updateProgressIndicators to hide when no active indicators
  updateProgressIndicators() {
    if (!this.isVisible) return;
    
    // Window height for calculations
    const windowHeight = window.innerHeight;
    const midpoint = windowHeight * this.config.offsetThreshold;
    
    // Create a map to find which visible section corresponds to each addon section
    const sectionToIndicatorMap = new Map();
    this.visibleSections.forEach((section, index) => {
      const addonIndex = this.addonSections.indexOf(section);
      if (addonIndex !== -1) {
        sectionToIndicatorMap.set(addonIndex, index);
      }
    });
    
    // Find which addon section is currently active
    let activeAddonSectionIndex = -1;
    let smallestDistance = Infinity;
    
    // Loop through all sections and find which one is most centered
    this.addonSections.forEach((section, index) => {
      const rect = section.getBoundingClientRect();
      const sectionMidpoint = rect.top + (rect.height / 2);
      
      // Find section with midpoint closest to viewport midpoint
      const distance = Math.abs(sectionMidpoint - midpoint);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        activeAddonSectionIndex = index;
      }
    });
    
    // Reset all indicators first
    this.indicators.forEach((indicator) => {
      indicator.classList.remove(this.config.activeClass);
    });
    
    // Track if we found an active indicator
    let foundActiveIndicator = false;
    
    // If we found an active section, highlight the corresponding indicator
    if (activeAddonSectionIndex !== -1) {
      // See if this section has a corresponding indicator
      const indicatorIndex = sectionToIndicatorMap.get(activeAddonSectionIndex);
      
      // Activate the corresponding indicator if found
      if (indicatorIndex !== undefined) {
        const indicator = this.indicators[indicatorIndex];
        if (indicator) {
          indicator.classList.add(this.config.activeClass);
          foundActiveIndicator = true;
        }
      }
    }
    
    // If no indicator is active, immediately hide the progress bar
    if (!foundActiveIndicator && this.isVisible) {
      this.isVisible = false;
      this.progressBar.style.opacity = '0';
      this.progressBar.style.pointerEvents = 'none';
    }
  }
  
  // Utility method to scroll to a specific addon section
  scrollToSection(index) {
    if (index >= 0 && index < this.addonSections.length) {
      const section = this.addonSections[index];
      const rect = section.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Scroll to position the section in the middle of the viewport
      const targetY = scrollTop + rect.top - (window.innerHeight / 2) + (rect.height / 2);
      
      window.scrollTo({
        top: targetY,
        behavior: 'smooth'
      });
    }
  }
  
  // Called if you want to hide the progress bar
  hide() {
    if (this.progressBar) {
      this.progressBar.style.opacity = '0';
      this.progressBar.style.pointerEvents = 'none';
      this.isVisible = false;
    }
  }
  
  // Called if you want to force-show the progress bar
  show() {
    if (this.progressBar) {
      this.progressBar.style.opacity = '1';
      this.progressBar.style.pointerEvents = 'auto';
      this.isVisible = true;
    }
  }
  
}

// Gallery Overlay Manager
// Enhanced Gallery Overlay Manager
class GalleryOverlayManager {
  constructor() {
    this.gallery = document.querySelector('.product__media-list');
    this.overlay = document.querySelector('.overlay-gallery');
    this.overlaySlider = this.overlay?.querySelector('.overlay-gallery__slider');
    this.closeButton = this.overlay?.querySelector('.overlay-gallery__close');
    this.prevButton = this.overlay?.querySelector('.overlay-gallery__prev');
    this.nextButton = this.overlay?.querySelector('.overlay-gallery__next');
    this.currentIndex = 0;
    this.visibleSlides = []; // Track which slides should be visible
    this.lastClickedImageSrc = null; // Track the last clicked image    
    this.currentMainProduct = null;
    this.currentEartips = null;
    this.currentChestpiece = null;
    this.activeOverlays = [];
    
    this.init();
  }
  
  init() {
    if (!this.gallery || !this.overlay) {
      return;
    }
    
    // Add click handler to gallery images
    const mediaItems = this.gallery.querySelectorAll('.product__media-item');
    mediaItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        // Don't open overlay if this image is hidden
        if (item.style.display === 'none') return;
        
        // Prevent default behavior if this is an image (not a video)
        const isImage = item.querySelector('img:not(.product-configurator-overlay-img)');
        if (isImage) {
          e.preventDefault();
          
          // Store the clicked image src
          this.lastClickedImageSrc = isImage.src;
          
          this.openOverlay();
        }
      });
    });

    // Add a listener to update overlays when configurator changes
    document.addEventListener('configurator:update', (e) => {
      if (e.detail) {
        // Update local state with configurator state
        this.currentMainProduct = e.detail.mainProduct;
        this.currentEartips = e.detail.eartips;
        this.currentChestpiece = e.detail.chestpiece;
        
        // If the overlay is open, update its overlays
        if (!this.overlay.classList.contains('hidden')) {
          this.updateOverlayWithConfigurator();
        }
      }
    });    

    // Add click handler to thumbnails to track which one was clicked
    const thumbnails = document.querySelectorAll('.thumbnail-list__item');
    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        // Find which image this thumbnail corresponds to
        const mediaId = thumb.dataset.target;
        const mediaItem = document.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
        if (mediaItem) {
          const img = mediaItem.querySelector('img');
          if (img) {
            // Store the src of the image this thumbnail points to
            this.lastClickedImageSrc = img.src;
          }
        }
      });
    });    
    
    // Add close handler
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.closeOverlay());
    }
    
    // Add keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.overlay.classList.contains('hidden')) {
        if (e.key === 'Escape') {
          this.closeOverlay();
        } else if (e.key === 'ArrowLeft') {
          this.navigate(-1);
        } else if (e.key === 'ArrowRight') {
          this.navigate(1);
        }
      }
    });
    
    // Add navigation button handlers
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.navigate(-1));
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.navigate(1));
    }
    
    // Add click outside to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.closeOverlay();
      }
    });
    
    // Add variant change listener to update gallery filtering
    this.listenForVariantChanges();
    
  }
  
  listenForVariantChanges() {
    // Listen for changes to main product variant
    const variantSelectors = document.querySelectorAll('.product-form__input select, .product-form__input input[type="radio"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        // Update the filtered slides when variant changes
        this.updateFilteredSlides();
      });
    });
    
    // Also listen for changes to addon variants that affect visibility
    const addonVariantRadios = document.querySelectorAll('.addon-variant-radio');
    addonVariantRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        // Small delay to allow other handlers to process first
        setTimeout(() => {
          this.updateFilteredSlides();
        }, 250);
      });
    });
    
    // Initial update
    this.updateFilteredSlides();
  }
  
  updateFilteredSlides() {
    // First, find which media items are currently visible in the main gallery
    const visibleMediaItems = Array.from(document.querySelectorAll('.product__media-item'));
    const visibleThumbnails = Array.from(document.querySelectorAll('.thumbnail-list__item')).filter(thumb => {
      const computedStyle = window.getComputedStyle(thumb);
      return computedStyle.display !== 'none';
    });
        
    // Get media IDs from visible thumbnails
    const visibleMediaIds = visibleThumbnails.map(thumb => thumb.dataset.target);
    
    // Now update the overlay gallery to match visible thumbnails
    const overlaySlides = this.overlaySlider.querySelectorAll('.overlay-gallery__slide');
    
    // Hide all slides first by default
    overlaySlides.forEach(slide => {
      slide.dataset.visible = 'false';
    });
    
    // Match each media item to its overlay slide based on image content
    visibleMediaItems.forEach(item => {
      // Check if this media item has a matching visible thumbnail
      if (visibleMediaIds.includes(item.dataset.mediaId)) {
        const img = item.querySelector('img');
        if (!img) return;
        
        const imgSrc = img.src;
        const imgAlt = img.alt;
        
        // Find matching overlay slide
        overlaySlides.forEach(slide => {
          const slideImg = slide.querySelector('img');
          if (!slideImg) return;
          
          // Match by src or alt text
          if (slideImg.src === imgSrc || slideImg.alt === imgAlt) {
            slide.dataset.visible = 'true';
          }
        });
      }
    });
    
    // If the overlay is currently open, make sure a visible slide is shown
    if (!this.overlay.classList.contains('hidden')) {
      this.ensureVisibleSlideShown();
    }
  }
  
  ensureVisibleSlideShown() {
    // Make sure at least one visible slide is displayed
    const overlaySlides = this.overlaySlider.querySelectorAll('.overlay-gallery__slide');
    const visibleSlides = Array.from(overlaySlides).filter(slide => slide.dataset.visible === 'true');
    
    if (visibleSlides.length > 0) {
      // Check if current slide is visible
      const currentSlide = overlaySlides[this.currentIndex];
      
      if (!currentSlide || currentSlide.dataset.visible !== 'true') {
        // Current slide is not visible, switch to the first visible one
        this.currentIndex = Array.from(overlaySlides).indexOf(visibleSlides[0]);
      }
      
      // Show the current slide
      this.showSlide(this.currentIndex);
    }
  }
  
  openOverlay() {
    // Make sure all visible thumbnails have corresponding visible slides
    this.updateFilteredSlides();
    
    let startIndex = 0;
    const overlaySlides = this.overlaySlider.querySelectorAll('.overlay-gallery__slide');
    const visibleSlides = Array.from(overlaySlides).filter(slide => slide.dataset.visible === 'true');
    
    // Try to find the slide that matches the last clicked image src
    if (this.lastClickedImageSrc) {
      
      // Extract the filename from the last clicked image src
      const lastClickedFilename = this.extractFilename(this.lastClickedImageSrc);
      
      // Look for a matching slide based on filename
      for (let i = 0; i < visibleSlides.length; i++) {
        const slideImg = visibleSlides[i].querySelector('img');
        if (slideImg) {
          const slideSrc = slideImg.src;
          const slideFilename = this.extractFilename(slideSrc);
          
          // Check if filenames match or if one contains the other (to handle naming variations)
          if (lastClickedFilename && slideFilename && 
              (lastClickedFilename.includes(slideFilename) || 
               slideFilename.includes(lastClickedFilename))) {
            // Found the matching slide - get its index in the full slides array
            startIndex = Array.from(overlaySlides).indexOf(visibleSlides[i]);
            break;
          }
        }
      }
      
      // If no match found by filename, try matching by alt text
      if (startIndex === 0) {
        const activeMediaItem = document.querySelector('.product__media-item.is-active');
        if (activeMediaItem) {
          const activeImg = activeMediaItem.querySelector('img');
          if (activeImg && activeImg.alt) {
            for (let i = 0; i < visibleSlides.length; i++) {
              const slideImg = visibleSlides[i].querySelector('img');
              if (slideImg && slideImg.alt === activeImg.alt) {
                startIndex = Array.from(overlaySlides).indexOf(visibleSlides[i]);
                break;
              }
            }
          }
        }
      }
    }
    
    // Set the current index
    this.currentIndex = startIndex;
    
    // Remove hidden class from overlay
    this.overlay.classList.remove('hidden');
    
    // Show the current slide
    this.showSlide(this.currentIndex);
    
    // Add configurator overlays if present
    this.updateOverlayWithConfigurator();
    
    // Prevent body scrolling
    document.body.style.overflow = 'hidden';
  }

  updateOverlayWithConfigurator() {
    // Only proceed if we have configuration options selected
    if (!this.currentEartips && !this.currentChestpiece) return;
    
    // Remove any existing overlays first
    this.removeOverlayConfiguratorOverlays();
    
    // Find the currently visible slide
    const currentSlide = this.overlaySlider.querySelector(`.overlay-gallery__slide[style*="display: flex"]`);
    if (!currentSlide) {
      return;
    }
    
    // Check if this is a config_main slide by looking at the image alt text
    const slideImg = currentSlide.querySelector('img');
    if (!slideImg || !slideImg.alt || !slideImg.alt.includes('for config_main')) {
      return;
    }
    
    // Create and add eartips overlay if selected
    if (this.currentEartips) {
      this.addConfiguratorOverlayToSlide(currentSlide, 'eartips', this.currentEartips);
    }
    
    // Create and add chestpiece overlay if selected
    if (this.currentChestpiece) {
      this.addConfiguratorOverlayToSlide(currentSlide, 'chestpiece', this.currentChestpiece);
    }
  }
  
  addConfiguratorOverlayToSlide(slide, overlayType, colorName) {
    // Find the matching overlay image based on variant
    const overlayImage = this.findConfiguratorOverlayImage(overlayType, colorName);
    if (!overlayImage) {
      return;
    }
        
    // Create overlay element
    const overlay = document.createElement('div');
    overlay.className = `overlay-config-overlay ${overlayType}-overlay`;
    overlay.dataset.overlayType = overlayType;
    overlay.dataset.color = colorName;
    
    // Position absolutely to cover the image
    overlay.style.cssText = `
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 5;
    `;
    
    // Create image element for overlay
    const img = document.createElement('img');
    img.className = 'overlay-configurator-overlay-img';
    img.src = overlayImage;
    img.alt = `${overlayType} overlay - ${colorName}`;
    img.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: contain;
    `;
    
    overlay.appendChild(img);
    slide.appendChild(overlay);
    
    // Keep track of the overlay
    this.activeOverlays.push(overlay);
  }
  
  findConfiguratorOverlayImage(overlayType, colorName) {
    // Try to find the matching overlay image from the main gallery first
    const mediaItems = document.querySelectorAll('.product__media-item');
    for (const item of mediaItems) {
      const img = item.querySelector('img');
      if (!img || !img.alt) continue;
      
      // Check if this is a matching overlay image
      const isMatchingOverlay = img.alt.includes(`for config_${overlayType}`) && 
                               img.alt.toLowerCase().includes(`color: ${colorName.toLowerCase()}`);
      
      if (isMatchingOverlay) {
        return img.src;
      }
    }
    
    // If not found in the gallery, try using the Product Configurator's data
    if (window.productConfigurator) {
      const overlayData = window.productConfigurator.overlayImages[overlayType][colorName.toLowerCase()];
      if (overlayData && overlayData.src) {
        return overlayData.src;
      }
    }
    
    return null;
  }
  
  removeOverlayConfiguratorOverlays() {
    // Remove all existing overlays
    this.activeOverlays.forEach(overlay => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    });
    
    this.activeOverlays = [];
  }  
  
  // Helper method to extract filename from URL
  extractFilename(url) {
    if (!url) return '';
    
    // First try to get the part before any query parameters
    let filename = url.split('?')[0];
    
    // Then extract just the filename from the path
    filename = filename.split('/').pop();
    
    // Remove any file extension
    filename = filename.split('.').slice(0, -1).join('.');
    
    return filename;
  }
  
  closeOverlay() {
    this.overlay.classList.add('hidden');
    document.body.style.overflow = '';
  }
  
  navigate(direction) {
    const overlaySlides = this.overlaySlider.querySelectorAll('.overlay-gallery__slide');
    const visibleSlides = Array.from(overlaySlides).filter(slide => slide.dataset.visible === 'true');
    
    if (visibleSlides.length === 0) {
      return;
    }
    
    // Find the current slide's position within visible slides
    const currentSlide = overlaySlides[this.currentIndex];
    let currentVisibleIndex = visibleSlides.indexOf(currentSlide);
    
    // If current slide is not in visible slides, start from beginning
    if (currentVisibleIndex === -1) {
      currentVisibleIndex = 0;
    }
    
    // Calculate new index
    let newVisibleIndex = currentVisibleIndex + direction;
    
    // Handle wrapping
    if (newVisibleIndex < 0) newVisibleIndex = visibleSlides.length - 1;
    if (newVisibleIndex >= visibleSlides.length) newVisibleIndex = 0;
    
    // Get the overall index of the new visible slide
    this.currentIndex = Array.from(overlaySlides).indexOf(visibleSlides[newVisibleIndex]);
        
    this.showSlide(this.currentIndex);
    
    // After navigation, update configurator overlays
    setTimeout(() => {
      this.updateOverlayWithConfigurator();
    }, 100);
    
  }
  
  showSlide(index) {
    const overlaySlides = this.overlaySlider.querySelectorAll('.overlay-gallery__slide');
    const visibleSlides = Array.from(overlaySlides).filter(slide => slide.dataset.visible === 'true');
    
    // First, hide all slides
    overlaySlides.forEach(slide => {
      slide.style.display = 'none';
    });
    
    // If no slides are visible, there's nothing to show
    if (visibleSlides.length === 0) {
      return;
    }
    
    // Make sure the index is valid
    if (index < 0 || index >= overlaySlides.length) {
      this.currentIndex = Array.from(overlaySlides).indexOf(visibleSlides[0]);
    } 
    // If the requested slide exists but is not visible, find the first visible one
    else if (overlaySlides[index] && overlaySlides[index].dataset.visible !== 'true') {
      this.currentIndex = Array.from(overlaySlides).indexOf(visibleSlides[0]);
    } 
    // Use the requested index if it's valid and visible
    else {
      this.currentIndex = index;
    }
    
    // Show the current slide if it exists
    if (overlaySlides[this.currentIndex]) {
      overlaySlides[this.currentIndex].style.display = 'flex';
      
      // Debug the slide state
      const slideImg = overlaySlides[this.currentIndex].querySelector('img');
      if (slideImg) {
      }
    }

    // Also update configurator overlays when changing slides
    setTimeout(() => {
      this.updateOverlayWithConfigurator();
    }, 100);
  }
  
}

// Run the enhanced fix on page load
document.addEventListener('DOMContentLoaded', () => {
  // Run the standard initialization first
  setTimeout(() => {
    enhancedScanForAddonSettings();
    improveVariantImageVisibility();
    
    // Add a final check after 1 second
    setTimeout(() => {
      
      // Look for add-on groups with hide_image=true and non-none selections
      const addonGroups = document.querySelectorAll('.addon-product-options[data-hide-image="true"]');
      addonGroups.forEach(group => {
        const handle = group.dataset.handle;
        if (!handle) return;
        
        const checkedRadio = group.querySelector('input[type="radio"]:checked');
        if (checkedRadio && checkedRadio.value !== 'none' && !checkedRadio.dataset.isNone) {
          window.emergencyFixProductImages(handle);
        }
      });
    }, 1000);
  }, 300);
});


// Add this function to your code to fix the image filtering issue
function fixProductImageFiltering() {  
  // Get the main product addon group (first one)
  const mainProductGroup = document.querySelector('.addon-product-options:first-child');
  if (!mainProductGroup) {
    return;
  }
  
  // Find all radio buttons in main product
  const radios = mainProductGroup.querySelectorAll(PRODUCT_CONFIG.addOn.addonVariantSelector);
  
  // Check if we have a configurator
  const hasConfigurator = window.productConfigurator && 
                         Object.keys(window.productConfigurator.configGroups || {}).length > 0;
    
  // If we don't have a configurator, we need to ensure variant images work
  if (!hasConfigurator) {
    // Add change event listeners to all main product variant radios
    radios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (radio.checked && radio.value !== 'none') {
          const variant = radio.value.toLowerCase();
          
          // Apply direct image filtering
          filterProductImagesByVariant(variant);
        }
      });
    });
    
    // Trigger filtering for the currently selected variant
    const selectedRadio = mainProductGroup.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
    if (selectedRadio && selectedRadio.value !== 'none') {
      filterProductImagesByVariant(selectedRadio.value.toLowerCase());
    }
  }
}

// Function to filter product images by variant
function filterProductImagesByVariant(variant) {  
  // Get all media items and thumbnails
  const mediaItems = document.querySelectorAll('.product__media-item');
  const thumbnails = document.querySelectorAll('.thumbnail-list__item');
  
  // Track if we found any matching images
  let foundMatchingImage = false;
  let firstMatchingImage = null;
  
  // First hide all images
  mediaItems.forEach(item => {
    item.classList.remove('is-active');
    item.style.display = 'none';
  });
  
  // Show images matching this variant or generic images (those without color specification)
  mediaItems.forEach(item => {
    const img = item.querySelector('img');
    if (!img) return;
    
    const altText = img.alt || '';
    
    // Check if this is a matching variant image
    const isMatchingVariant = altText.toLowerCase().includes(`color: ${variant}`) || 
                              altText.toLowerCase().match(new RegExp(`\\bcolor:\\s*${variant}\\b`, 'i'));
    
    // Also show generic images that don't specify a color
    const isGenericImage = !altText.includes('Color:');
    
    // Always hide configurator-specific images 
    const isConfigImage = altText.includes('for config_');
    
    if ((isMatchingVariant || isGenericImage) && !isConfigImage) {
      // Show this image
      item.style.display = item.dataset.originalDisplay || '';
      
      // Track that we found a matching image
      foundMatchingImage = true;
      
      // Keep track of the first matching variant-specific image
      if (isMatchingVariant && !firstMatchingImage) {
        firstMatchingImage = item;
      }
    }
  });
  
  // Activate the first matching variant-specific image if found
  if (firstMatchingImage) {
    mediaItems.forEach(item => item.classList.remove('is-active'));
    firstMatchingImage.classList.add('is-active');
    
    // Also update thumbnails
    const mediaId = firstMatchingImage.dataset.mediaId;
    thumbnails.forEach(thumb => {
      thumb.classList.remove('is-active');
      if (thumb.dataset.target === mediaId) {
        thumb.classList.add('is-active');
      }
    });
  } else if (foundMatchingImage) {
    // If no variant-specific image but found generic ones, activate the first visible
    const firstVisible = Array.from(mediaItems).find(item => item.style.display !== 'none');
    if (firstVisible) {
      mediaItems.forEach(item => item.classList.remove('is-active'));
      firstVisible.classList.add('is-active');
      
      // Also update thumbnails
      const mediaId = firstVisible.dataset.mediaId;
      thumbnails.forEach(thumb => {
        thumb.classList.remove('is-active');
        if (thumb.dataset.target === mediaId) {
          thumb.classList.add('is-active');
        }
      });
    }
  }
  
  // Update thumbnail visibility to match images
  updateThumbnailVisibility();
}

// Helper function to update thumbnail visibility
function updateThumbnailVisibility() {
  const mediaItems = document.querySelectorAll('.product__media-item');
  const thumbnails = document.querySelectorAll('.thumbnail-list__item');
  
  // Hide/show thumbnails based on corresponding media visibility
  thumbnails.forEach(thumb => {
    const mediaId = thumb.dataset.target;
    const mediaItem = document.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
    
    if (mediaItem) {
      // Match thumbnail visibility with media item
      thumb.style.display = (mediaItem.style.display === 'none') ? 'none' : '';
    }
  });
  
  // Ensure at least one thumbnail is active
  const activeThumb = document.querySelector('.thumbnail-list__item.is-active');
  if (!activeThumb) {
    const firstVisibleThumb = Array.from(thumbnails).find(
      thumb => thumb.style.display !== 'none'
    );
    
    if (firstVisibleThumb) {
      firstVisibleThumb.classList.add('is-active');
    }
  }
}


// Enhanced image filtering solution for products without configurator
function enhancedProductImageFiltering() {
  // Get the main product addon group (first one)
  const mainProductGroup = document.querySelector('.addon-product-options:first-child');
  if (!mainProductGroup) {
    return;
  }
  
  // Check if we have a configurator
  const hasConfigurator = window.productConfigurator && 
                         window.productConfigurator.configGroups && 
                         window.productConfigurator.configGroups.size > 0;
    
  // Only proceed if we don't have a configurator
  if (hasConfigurator) {
    return;
  }
  
  // Directly patch the AddOnProductManager.onVariantChange method to ensure filtering happens
  if (window.addonManager) {
    // Store original method
    const originalOnVariantChange = window.addonManager.onVariantChange;
    
    // Replace with enhanced version
    window.addonManager.onVariantChange = function(radio) {
      // Call original method first
      originalOnVariantChange.call(this, radio);
      
      // Check if this is a main product variant change
      const productId = radio.closest('[data-product-id]')?.dataset.productId;
      
      if (this.primaryAddonGroup && productId === this.primaryAddonGroup.dataset.productId) {
        if (radio.checked && radio.value !== 'none') {
          const variant = radio.value.toLowerCase();
          
          // Apply direct image filtering
          enhancedFilterProductImagesByVariant(variant);
        }
      }
    };
    
    // Trigger initial filtering
    const selectedRadio = mainProductGroup.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
    if (selectedRadio && selectedRadio.value !== 'none') {
      enhancedFilterProductImagesByVariant(selectedRadio.value.toLowerCase());
    }
  } else {
    console.warn('addonManager not found, cannot patch onVariantChange');
  }
}

// Enhanced filtering function with better handling of thumbnails and active state
function enhancedFilterProductImagesByVariant(variant) {
  
  // Get all media items and thumbnails
  const mediaItems = document.querySelectorAll('.product__media-item');
  const thumbnails = document.querySelectorAll('.thumbnail-list__item');
  
  // Track if we found any matching images
  let foundMatchingImages = [];
  
  // First hide all images
  mediaItems.forEach(item => {
    // Don't remove active class yet, just hide
    item.style.display = 'none';
  });
  
  // Find matching variant images and generic images
  mediaItems.forEach(item => {
    const img = item.querySelector('img');
    if (!img) return;
    
    const altText = img.alt || '';
    
    // Check if this is a matching variant image - more precise regex matching
    const isMatchingVariant = 
      altText.toLowerCase().includes(`color: ${variant}`) || 
      altText.toLowerCase().match(new RegExp(`\\bcolor:\\s*${variant}\\b`, 'i'));
    
    // Also show generic images that don't specify a color
    const isGenericImage = !altText.includes('Color:');
    
    // Always hide configurator-specific images 
    const isConfigImage = altText.includes('for config_');
    
    // Handle addon product images (with handle: in alt text)
    const isAddonImage = altText.includes('handle:') || altText.includes('handle: ');
    
    // Show if: it's a matching variant OR a generic image, but NOT a config or addon image
    if ((isMatchingVariant || isGenericImage) && !isConfigImage && !isAddonImage) {
      // Show this image
      item.style.display = item.dataset.originalDisplay || '';
      
      // Keep track of matching variant-specific images with higher priority than generic
      if (isMatchingVariant) {
        foundMatchingImages.push({
          item: item,
          priority: 1 // Higher priority for variant-specific
        });
      } else if (isGenericImage) {
        foundMatchingImages.push({
          item: item,
          priority: 0 // Lower priority for generic
        });
      }
    }
  });
  
  // Sort matching images by priority (variant-specific first)
  foundMatchingImages.sort((a, b) => b.priority - a.priority);
  
  // Activate the highest priority image
  if (foundMatchingImages.length > 0) {
    // Remove active class from all media items first
    mediaItems.forEach(item => item.classList.remove('is-active'));
    
    // Activate the highest priority image
    const highestPriorityImage = foundMatchingImages[0].item;
    highestPriorityImage.classList.add('is-active');
    
    // Ensure it's visible
    highestPriorityImage.style.display = highestPriorityImage.dataset.originalDisplay || '';
    
    // Update the active thumbnail
    const mediaId = highestPriorityImage.dataset.mediaId;
    if (mediaId) {
      thumbnails.forEach(thumb => {
        thumb.classList.remove('is-active');
        
        // Match thumbnail visibility with media
        if (thumb.dataset.target === mediaId) {
          thumb.classList.add('is-active');
          thumb.style.display = '';
        }
      });
    }
  }
  
  // Update all thumbnail visibility to match image visibility
  enhancedUpdateThumbnailVisibility();
}

// Enhanced thumbnail visibility function
function enhancedUpdateThumbnailVisibility() {
  const mediaItems = document.querySelectorAll('.product__media-item');
  const thumbnails = document.querySelectorAll('.thumbnail-list__item');
  
  // First hide all thumbnails by default
  thumbnails.forEach(thumb => {
    thumb.style.display = 'none';
  });
  
  // Only show thumbnails corresponding to visible media items
  thumbnails.forEach(thumb => {
    const mediaId = thumb.dataset.target;
    const mediaItem = document.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
    
    if (mediaItem) {
      // Get computed display style, not just inline
      const computedDisplay = window.getComputedStyle(mediaItem).display;
      const isVisible = computedDisplay !== 'none';
      
      // Match thumbnail visibility with media item
      thumb.style.display = isVisible ? '' : 'none';
    }
  });
  
  // Ensure at least one thumbnail is active
  const activeThumb = document.querySelector('.thumbnail-list__item.is-active');
  if (!activeThumb) {
    const firstVisibleThumb = Array.from(thumbnails).find(
      thumb => window.getComputedStyle(thumb).display !== 'none'
    );
    
    if (firstVisibleThumb) {
      firstVisibleThumb.classList.add('is-active');
      
      // Also activate corresponding media item
      const mediaId = firstVisibleThumb.dataset.target;
      const mediaItem = document.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
      if (mediaItem) {
        mediaItems.forEach(item => item.classList.remove('is-active'));
        mediaItem.classList.add('is-active');
      }
    }
  }
}

// Enhanced patching method that works with the existing code
function patchAddOnProductManager() {
  // Wait until the AddOnProductManager is initialized
  if (!window.addonManager) {
    setTimeout(patchAddOnProductManager, 100);
    return;
  }
  
  // Apply the enhanced filtering
  enhancedProductImageFiltering();
}

// This is an improved version of the direct fix - this should be added to initialize functions
document.addEventListener('DOMContentLoaded', () => {
  // Wait for main initialization to complete
  setTimeout(() => {
    patchAddOnProductManager();
  }, 1000);
});
// Call this function from your initialization code
document.addEventListener('DOMContentLoaded', () => {
  // Wait for everything else to initialize first
  setTimeout(() => {
    fixProductImageFiltering();
  }, 500);
});

// Initialize everything when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Scan for addon settings first
  scanForAddonSettings();

  // Add a style element to hide thumbnails by default on page load
  const style = document.createElement('style');
  style.id = 'initial-thumbnail-hide';
  style.textContent = `
    .thumbnail-list__item {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
  
  // Remove the style after a short delay when all JS has run
  setTimeout(() => {
    const styleElem = document.getElementById('initial-thumbnail-hide');
    if (styleElem) {
      styleElem.remove();
    }
  }, 1000);  
  
  // Initialize basic product functionality
  new ProductDescriptionManager();
  const addonManager = new AddOnProductManager();
  new AddOnMembershipHandler();
  
  window.productPriceManager = new ProductPriceManager();

  new ShippingETAManager(); 
  
  // Initialize the new product configurator
  const configurator = new ProductConfigurator();  
  
  // Initialize the product progress bar
  const progressBar = new ProductProgressBar();

  // Initialize gallery overlay
  const galleryOverlay = new GalleryOverlayManager();  

  const shippingManager = new ShippingETAManager();

  // If product configurator exists, share its state with the gallery overlay
  if (window.productConfigurator) {
    galleryOverlay.currentMainProduct = window.productConfigurator.currentMainProduct;
    galleryOverlay.currentEartips = window.productConfigurator.currentEartips;
    galleryOverlay.currentChestpiece = window.productConfigurator.currentChestpiece;
  }  
  
  // Optionally enable interaction with the progress bar
  // progressBar.enableInteraction();
  
  // Make managers globally accessible for debugging
  window.productConfigurator = configurator;
  window.addonManager = addonManager;
  window.productProgressBar = progressBar;
  window.galleryOverlay = galleryOverlay;

  // Function to initialize addon active states
  function initializeAddonActiveStates() {
    // Handle engraving specifically
    const engravingContainer = document.querySelector('.addon-product-options[data-handle="stethoscope-engraving"]');
    if (engravingContainer) {
      const noEngravingRadio = engravingContainer.querySelector('input[name="engraving-enabled"][value="none"]');
      const addEngravingRadio = engravingContainer.querySelector('input[name="engraving-enabled"][value="enabled"]');
      
      // Make sure No Engraving is checked and has active state by default
      if (noEngravingRadio && !addEngravingRadio.checked) {
        noEngravingRadio.checked = true;
        
        // Add active class to the no-engraving button
        const noEngravingLabel = noEngravingRadio.closest('.variant-button-label');
        if (noEngravingLabel) {
          noEngravingLabel.classList.add('variant-button-active');
        }
      }
      
      // If we already have engraving enabled, force a price update
      if (addEngravingRadio && addEngravingRadio.checked) {
        if (window.addonManager) {
          window.addonManager.onVariantChange(addEngravingRadio);
        }
      }
    }
    
    // For all other addon products (not the main product), ensure "none" options have active state
    const addonGroups = document.querySelectorAll('.addon-product-options:not(:first-child)');
    addonGroups.forEach(group => {
      // Skip engraving as we handled it separately
      if (group.dataset.handle === 'stethoscope-engraving') return;
      
      // Check if any option is already selected
      const checkedRadio = group.querySelector(`${PRODUCT_CONFIG.addOn.addonVariantSelector}:checked`);
      
      // If nothing is checked, default to "none" option
      if (!checkedRadio) {
        const noneOption = Array.from(
          group.querySelectorAll(PRODUCT_CONFIG.addOn.addonVariantSelector)
        ).find(radio => radio.dataset.isNone === 'true' || radio.value.toLowerCase() === 'none');
        
        if (noneOption) {
          noneOption.checked = true;
          
          // Add active class to the none option label
          const noneLabel = noneOption.closest('.variant-swatch-label, .variant-button-label');
          if (noneLabel) {
            if (noneLabel.classList.contains('variant-swatch-label')) {
              noneLabel.classList.add('swatch-active');
            } else {
              noneLabel.classList.add('variant-button-active');
            }
          }
        }
      } else {
        // If something is already checked, ensure it has the active class
        const checkedLabel = checkedRadio.closest('.variant-swatch-label, .variant-button-label');
        if (checkedLabel) {
          if (checkedLabel.classList.contains('variant-swatch-label')) {
            checkedLabel.classList.add('swatch-active');
          } else {
            checkedLabel.classList.add('variant-button-active');
          }
        }
      }
    });
  }  
  
  // Initialize addon active states after a short delay to ensure DOM is fully processed
  setTimeout(initializeAddonActiveStates, 100);

  // Add to your initialization code
  window.checkGalleryOverlay = function() {
    const visibleThumbs = Array.from(document.querySelectorAll('.thumbnail-list__item')).filter(
      t => window.getComputedStyle(t).display !== 'none'
    );
    
    const overlaySlides = document.querySelectorAll('.overlay-gallery__slide');
    const visibleSlides = Array.from(overlaySlides).filter(s => s.dataset.visible === 'true');
    
    return 'Check complete. See console for details.';
  };
  
  // Helper function to manually ensure Image: images are visible
  window.showConfigImages = function() {
    if (!window.productConfigurator) {
      return 'Product configurator not initialized';
    }
    
    const configurator = window.productConfigurator;
    
    if (!configurator.lastConfigType || !configurator.lastConfigValue) {
      return 'No config type/value set';
    }
    
    let count = 0;
    
    // Find and show all matching Image: images
    document.querySelectorAll('.product__media-item').forEach(item => {
      const img = item.querySelector('img');
      if (!img) return;
      
      const altText = img.alt || '';
      
      if (altText.includes('Image:') && 
          altText.toLowerCase().includes(configurator.lastConfigValue.toLowerCase()) && 
          altText.toLowerCase().includes(`for ${configurator.lastConfigType.toLowerCase()}`)) {
        
        item.style.display = item.dataset.originalDisplay || '';
        item.style.removeProperty('display');
        count++;
        
        // Also show corresponding thumbnail
        const mediaId = item.dataset.mediaId;
        if (mediaId) {
          const thumb = document.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]`);
          if (thumb) {
            thumb.style.display = thumb.dataset.originalDisplay || '';
            thumb.style.removeProperty('display');
          }
        }
      }
    });
    
    return `Made ${count} configured images visible`;
  };  
  
  // Auto-scan product gallery images for debug purposes
  setTimeout(() => {
    addonManager.logAllProductImages();
  }, 500);

  setTimeout(() => {
    preventConfigFlickering();
  }, 1000);  
  
  // Add additional event listeners to addon option labels for more reliable detection
  setTimeout(() => {
    // For each add-on group with hide_image=true
    const addonGroups = document.querySelectorAll('.addon-product-options[data-hide-image="true"]');
    
    addonGroups.forEach(group => {
      const handle = group.dataset.handle;
      if (!handle) return;
      
      // Add direct click event handlers to the labels
      const optionLabels = group.querySelectorAll('.variant-button-label, .variant-swatch-label');
      optionLabels.forEach(label => {
        label.addEventListener('click', () => {
          const radio = label.querySelector('input[type="radio"]');
          if (radio && radio.checked) {
            const isNone = radio.dataset.isNone === 'true' || radio.value.toLowerCase() === 'none';
                        
            if (!isNone) {
              // Use setTimeout to ensure radio button state is updated
              setTimeout(() => {
                window.emergencyFixProductImages(handle);
              }, 100);
            }
          }
        });
      });
    });
  }, 1000);  

  // Add this after your other initializations
  setTimeout(() => {
    // Get the thumbnail list in the pdp-main-thumbnails
    const thumbnailList = document.querySelector('.pdp-main-thumbnails .thumbnail-list');
    if (!thumbnailList) return;
    
    // Add a universal click event listener
    thumbnailList.addEventListener('click', (e) => {
      const thumbnailItem = e.target.closest('.thumbnail-list__item');
      if (!thumbnailItem) return;
      
      // Get the target media ID this thumbnail points to
      const mediaId = thumbnailItem.dataset.target;
      if (!mediaId) return;
      
      // Find all slides in the pdp-main-image
      const allSlides = document.querySelectorAll('.pdp-main-image .product__media-item');
      
      // Find the target slide
      const targetSlide = document.querySelector(`.pdp-main-image .product__media-item[data-media-id="${mediaId}"]`);
      if (!targetSlide) return;
      
      // Update active states for all slides
      allSlides.forEach(slide => {
        // First remove active class from all slides
        slide.classList.remove('is-active');
        
        // For slides that aren't the target, ensure display: none !important is set
        if (slide !== targetSlide) {
          slide.style.setProperty('display', 'none', 'important');
        }
      });
      
      // Make target slide active and visible
      targetSlide.classList.add('is-active');
      targetSlide.style.setProperty('display', 'block', 'important');
      
      // Update thumbnail active states
      const allThumbnails = document.querySelectorAll('.pdp-main-thumbnails .thumbnail-list__item');
      allThumbnails.forEach(thumb => {
        thumb.classList.remove('is-active');
      });
      thumbnailItem.classList.add('is-active');
    });
    
  }, 1000); // Run after other initializations  
  
});

// Enhanced Product Video Manager with Thumbnail Cleanup Fix
class EnhancedProductVideoManager {
  constructor() {
    // Find all product media items that might contain videos
    this.mediaItems = document.querySelectorAll('.product__media-item');
    this.videoOverlay = document.querySelector('#product-video-overlay');
    this.thumbnailList = document.querySelector('.thumbnail-list');
    this.activeVideoMedia = null;
    this.isVideoPlaying = false;
    
    this.init();
  }
  
  init() {
    if (!this.mediaItems.length) {
      return;
    }
    
    // Process each media item to handle videos correctly
    this.mediaItems.forEach(mediaItem => {
      // Check if this media item contains a video (has deferred-media)
      const deferredMedia = mediaItem.querySelector('deferred-media');
      const productModel = mediaItem.querySelector('product-model');
      const galleryLink = mediaItem.querySelector('.open-overlay-gallery');
      
      if ((deferredMedia || productModel) && galleryLink) {
        // This is a video slide - hide the gallery overlay link
        galleryLink.style.display = 'none';
        
        // Make sure the deferred media is visible and clickable
        if (deferredMedia) {
          deferredMedia.style.display = 'block';
          deferredMedia.style.width = '100%';
          deferredMedia.style.height = '100%';
          
          // Add any additional styling to position the play button correctly
          const playButton = deferredMedia.querySelector('.deferred-media__poster-button');
          if (playButton) {
            playButton.style.position = 'absolute';
            playButton.style.inset = '0';
            playButton.style.display = 'flex';
            playButton.style.alignItems = 'center';
            playButton.style.justifyContent = 'center';
            playButton.style.top = '50%';
            playButton.style.left = '50%';
            
            // Add click handler to track when a video is activated
            playButton.addEventListener('click', () => {
              this.handleVideoActivation(mediaItem);
            });
          }
        }
        
        if (productModel) {
          productModel.style.display = 'block';
          productModel.style.width = '100%';
          productModel.style.height = '100%';
        }
      }
    });
    
    // Add thumbnail click handler to clean up videos when changing slides
    if (this.thumbnailList) {
      this.thumbnailList.addEventListener('click', (e) => {
        const clickedThumbnail = e.target.closest('.thumbnail-list__item');
        if (clickedThumbnail) {
          // Clean up any active videos when switching slides
          this.cleanupActiveVideo();
        }
      });
    }
    
    // Also add click handler to the main product slides to handle direct clicks
    const productMediaList = document.querySelector('.product__media-list');
    if (productMediaList) {
      productMediaList.addEventListener('click', (e) => {
        const clickedMedia = e.target.closest('.product__media-item');
        if (clickedMedia && !clickedMedia.classList.contains('is-active')) {
          // If we clicked a non-active media item, clean up any active videos
          this.cleanupActiveVideo();
        }
      });
    }
    
    // Handle the video overlay close button
    if (this.videoOverlay) {
      const closeButton = this.videoOverlay.querySelector('.close-video-overlay');
      if (closeButton) {
        closeButton.addEventListener('click', () => this.closeVideoOverlay());
      }
      
      // Also close when clicking outside the video container
      this.videoOverlay.addEventListener('click', (e) => {
        if (e.target === this.videoOverlay) {
          this.closeVideoOverlay();
        }
      });
      
      // Add keyboard listener for Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.videoOverlay.classList.contains('hidden')) {
          this.closeVideoOverlay();
        }
      });
    }
    
    // Track navigation with next/prev buttons
    const prevButton = document.querySelector('.slider-button--prev');
    const nextButton = document.querySelector('.slider-button--next');
    
    if (prevButton) {
      prevButton.addEventListener('click', () => this.cleanupActiveVideo());
    }
    
    if (nextButton) {
      nextButton.addEventListener('click', () => this.cleanupActiveVideo());
    }
    
    // MutationObserver to detect when slides change active status
    this.observeActiveSlideChanges();
    
    // Ensure we catch any dynamically created deferred-media elements
    this.observeDynamicElements();
  }
  
  handleVideoActivation(mediaItem) {
    this.activeVideoMedia = mediaItem;
    this.isVideoPlaying = true;
    
    // Store the template content to use when resetting
    if (!mediaItem.dataset.originalTemplateContent) {
      const template = mediaItem.querySelector('template');
      if (template) {
        mediaItem.dataset.originalTemplateContent = template.innerHTML;
      }
    }
  }
  
  cleanupActiveVideo() {
    if (!this.activeVideoMedia || !this.isVideoPlaying) return;
        
    // Find existing video elements and clean them up
    const videoElements = this.activeVideoMedia.querySelectorAll('video, iframe');
    videoElements.forEach(video => {
      if (video.tagName === 'VIDEO') {
        video.pause();
        video.currentTime = 0;
        video.src = '';
        video.load();
      }
      
      if (video.tagName === 'IFRAME') {
        // Reset iframe src to stop the video
        const currentSrc = video.src;
        if (currentSrc) {
          video.src = '';
          setTimeout(() => {
            video.remove();
          }, 50);
        }
      }
      
      // Remove the video element
      try {
        video.remove();
      } catch (e) {
        console.error('Error removing video element:', e);
      }
    });
    
    // Find the deferred-media element
    const deferredMedia = this.activeVideoMedia.querySelector('deferred-media');
    if (deferredMedia) {
      // Reset the deferred-media by recreating its original state
      
      // First, create a placeholder for the original DOM structure
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = `
        <div id="Deferred-Poster-${deferredMedia.dataset.mediaId}" class="product__media media">
          <span class="deferred-media__poster-button motion-reduce">
            <span class="visually-hidden">Play video</span>
            <span class="svg-wrapper">
              <svg class="icon icon-play" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9 0C4.03636 0 0 4.03636 0 9C0 13.9636 4.03636 18 9 18C13.9636 18 18 13.9636 18 9C18 4.03636 13.9636 0 9 0ZM6 4.5L13.5 9L6 13.5V4.5Z" fill="currentColor"/>
              </svg>
            </span>
          </span>
          <!-- Preview image placeholder -->
        </div>
        <template>
          ${this.activeVideoMedia.dataset.originalTemplateContent || ''}
        </template>
      `;
      
      // Get the original preview image
      const originalImg = deferredMedia.querySelector('img');
      if (originalImg && tempContainer.querySelector('.product__media')) {
        // Clone the original image
        const imgClone = originalImg.cloneNode(true);
        tempContainer.querySelector('.product__media').appendChild(imgClone);
      }
      
      // Clear and rebuild the deferred-media element
      deferredMedia.innerHTML = '';
      deferredMedia.appendChild(tempContainer.querySelector('.product__media'));
      deferredMedia.appendChild(tempContainer.querySelector('template'));
      
      // Re-add play button click handler
      const newPlayButton = deferredMedia.querySelector('.deferred-media__poster-button');
      if (newPlayButton) {
        newPlayButton.addEventListener('click', () => {
          this.handleVideoActivation(this.activeVideoMedia);
        });
      }
    }
    
    // Reset tracking state
    this.isVideoPlaying = false;
    this.activeVideoMedia = null;    
  }
  
  observeActiveSlideChanges() {
    // Create a MutationObserver to detect when active slide changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        // Look for class changes
        if (mutation.type === 'attributes' && 
            mutation.attributeName === 'class') {
          const target = mutation.target;
          
          // If a slide is becoming inactive and it has a video, clean it up
          if (target.classList.contains('product__media-item') && 
              !target.classList.contains('is-active') && 
              (target.querySelector('deferred-media') || target.querySelector('product-model'))) {
            if (target === this.activeVideoMedia) {
              this.cleanupActiveVideo();
            }
          }
        }
      });
    });
    
    // Observe all media items for class changes
    this.mediaItems.forEach(mediaItem => {
      observer.observe(mediaItem, { attributes: true });
    });
  }
  
  closeVideoOverlay() {
    if (!this.videoOverlay) return;
    
    // Add hidden class to overlay
    this.videoOverlay.classList.add('hidden');
    
    // Find and stop any playing videos
    const videoContainer = this.videoOverlay.querySelector('.video-container');
    if (videoContainer) {
      // Handle iframe videos (YouTube, Vimeo, etc.)
      const iframes = videoContainer.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        // Get the current src
        const currentSrc = iframe.src;
        
        // Reset the iframe src to stop the video
        if (currentSrc) {
          iframe.src = currentSrc;
        }
      });
      
      // Handle HTML5 videos
      const videos = videoContainer.querySelectorAll('video');
      videos.forEach(video => {
        video.pause();
        video.currentTime = 0;
      });
      
      // Clear the container
      videoContainer.innerHTML = '';
    }
    
    // Re-enable body scrolling
    document.body.style.overflow = '';
  }
  
  observeDynamicElements() {
    // Use MutationObserver to watch for dynamically added elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            // Check if the added node is a deferred-media or contains one
            if (node.nodeType === 1) { // Element node
              if (node.matches('deferred-media')) {
                this.handleDeferredMedia(node);
              } else {
                const deferredMedia = node.querySelectorAll('deferred-media');
                deferredMedia.forEach(media => this.handleDeferredMedia(media));
              }
            }
          });
        }
      });
    });
    
    // Start observing the entire document
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  handleDeferredMedia(deferredMedia) {
    // Find the parent media item
    const mediaItem = deferredMedia.closest('.product__media-item');
    if (!mediaItem) return;
    
    // Find and hide the gallery link if it exists
    const galleryLink = mediaItem.querySelector('.open-overlay-gallery');
    if (galleryLink) {
      galleryLink.style.display = 'none';
    }
    
    // Make sure the deferred media is visible and clickable
    deferredMedia.style.display = 'block';
    deferredMedia.style.width = '100%';
    deferredMedia.style.height = '100%';
    
    // Style the play button
    const playButton = deferredMedia.querySelector('.deferred-media__poster-button');
    if (playButton) {
      playButton.style.position = 'absolute';
      playButton.style.inset = '0';
      playButton.style.display = 'flex';
      playButton.style.alignItems = 'center';
      playButton.style.justifyContent = 'center';
      
      // Add click handler to track when a video is activated
      playButton.addEventListener('click', () => {
        this.handleVideoActivation(mediaItem);
      });
    }
    
    // Store the template content for later reset
    const template = deferredMedia.querySelector('template');
    if (template && !mediaItem.dataset.originalTemplateContent) {
      mediaItem.dataset.originalTemplateContent = template.innerHTML;
    }
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  // Wait for the original video manager to initialize first, then enhance it
  setTimeout(() => {
    window.enhancedProductVideoManager = new EnhancedProductVideoManager();
  }, 1000);  
});

// Also initialize now in case the DOM is already loaded
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  setTimeout(() => {
    window.enhancedProductVideoManager = new EnhancedProductVideoManager();
  }, 1000);
}